/* Photo Markup — client-side annotation engine
 * Canvas: Konva.js / Strokes: perfect-freehand / Shape recog: self-written
 * All state lives on window.PM for easy console debugging.
 */
(function(){
    'use strict';

    const I18N = window.PM_I18N || {};
    const $  = (id) => document.getElementById(id);
    const $$ = (sel, root) => Array.from((root || document).querySelectorAll(sel));
    // The read-out spans were replaced by editable number fields (which show the
    // value themselves); keep the writes harmless where a span no longer exists.
    const setVal = (id, v) => { const el = document.getElementById(id); if (el) el.textContent = v; };
    // Number fields emit an empty string while the user is mid-edit.
    const num = (e) => { const v = e.target.value; const n = +v; return (v === '' || !Number.isFinite(n)) ? null : n; };

    const app = {
        state: {
            tool: 'select',
            color: '#ff3b30',
            recent: [],
            stroke: 4,
            opacity: 1,
            fontFamily: 'system-ui, -apple-system, sans-serif',
            fontSize: 24,
            textBg: false,
            textOutline: false,
            outlineColor: '#000000',
            shapeFill: false,
            arrowStyle: 'single',
            arrowDashed: false,
            blurRadius: 12,
            pixelSize: 12,
            spotlightShape: 'rect',
            spotlightDim: 0.65,
            counter: 1,
            zoom: 1,
            imgW: 0,
            imgH: 0,
            imageSource: null,
            adjust: { brightness: 0, contrast: 0, saturation: 0, blurAll: 0 },
            cropRatio: 0,
            retina: 1,
        },
        konva: {
            stage: null,
            bgLayer: null,
            imageNode: null,
            drawLayer: null,
            overlayLayer: null,
            transformer: null,
        },
        history: { stack: [], ptr: -1, capacity: 60, busy: false },
        ui: {
            panStart: null,
            spaceHeld: false,
            drawing: null,          // { node, type, points, start }
            editingText: null,
        },
        vendorReady: { konva: false, pf: false },
    };
    window.PM = app;
    // Surface for tools-extra.js: the few internals a new tool genuinely needs.
    // Nothing here is a new abstraction — these are the same helpers the
    // built-in tools call.
    app.api = {};

    // ────────────── telemetry ──────────────
    // Layout declares window.__ae / __aeToolComplete / __aeToolError (app.blade.php ~line 1221).
    // Wrapper handles absence of __ae (analytics disabled / DNT set) gracefully.
    function trackFeature(action, extra) {
        try {
            if (window.__ae) window.__ae('feature_use', Object.assign({ slug: 'photo-markup', action }, extra || {}));
        } catch(_) {}
    }
    function trackError(err, ctx, extra) {
        try {
            if (window.__aeToolError) {
                window.__aeToolError(Object.assign({
                    slug: 'photo-markup',
                    msg: String(err && (err.message || err) || '').slice(0, 200),
                    ctx: ctx || 'unknown',
                }, extra || {}));
            }
        } catch(_) {}
    }
    // The tool declares its result; «the file reached the device» is the download
    // door's to say (it fires `file-downloaded` itself, only for a delivered file —
    // the copy to the clipboard is not a file at all).
    function trackComplete(extra) {
        try {
            if (window.__aeToolComplete) window.__aeToolComplete(Object.assign({ slug: 'photo-markup' }, extra || {}));
        } catch(_) {}
    }
    // Dedup: one 'tool_used' per session per tool kind
    const _toolUsedSent = new Set();
    function trackToolUsed(toolName) {
        if (_toolUsedSent.has(toolName)) return;
        _toolUsedSent.add(toolName);
        trackFeature('tool_first_use', { tool: toolName });
    }

    // ────────────── toast ──────────────
    let toastTimer;
    function toast(msg, timeout) {
        // Callers pass a kind ('error') here too; a string as the timeout hid the
        // toast at once, so the share failure was never on screen.
        timeout = (typeof timeout === 'number' && timeout > 0) ? timeout : 2200;
        const el = $('pmToast');
        el.textContent = msg;
        el.hidden = false;
        clearTimeout(toastTimer);
        toastTimer = setTimeout(() => { el.hidden = true; }, timeout);
    }
    // A "building…" toast ends with the work, not with its 30 s timer.
    function untoast() {
        clearTimeout(toastTimer);
        const el = $('pmToast');
        if (el) el.hidden = true;
    }

    // ────────────── i18n fmt ──────────────
    function fmt(s, vars) {
        return String(s || '').replace(/\{(\w+)\}/g, (_, k) => (vars && k in vars) ? vars[k] : '');
    }

    // ────────────── vendor readiness ──────────────
    // The stage needs Konva and nothing else. Until 04.11.2026 it also waited —
    // without a bound, like Konva itself — for perfect-freehand, the pen's
    // outline engine, while the editor was revealed the moment a picture
    // opened. When either file was slow or never came, the person got every
    // button and none of them worked: each press read a stage that did not
    // exist (Sentry T3 «reading 'width'» in setZoom, T4 «reading 'nodes'» in
    // exportPNG, 03.11.2026; both files served 200 — it was the person's
    // network).
    //
    // Now Konva is waited for through the layout's door (__whenGlobal: visible
    // time, and it hears the fallback ladder's late copy). While it is on its
    // way the editor is inert and says so; if it never comes the person reads
    // why and gets a way out; a copy that arrives after that still brings the
    // editor up — for the picture opened LAST. perfect-freehand gates nothing:
    // the pen draws a plain stroke until it is here (see the pen tool).
    const KONVA_WAIT_MS = 30000;
    function konvaHere() { return !!(window.Konva && typeof window.Konva.Stage === 'function'); }
    function pfReady() { return !!(window.PerfectFreehand && typeof window.PerfectFreehand.getStroke === 'function'); }
    if (pfReady()) app.vendorReady.pf = true;
    else window.addEventListener('pm:pf-ready', () => { app.vendorReady.pf = pfReady(); }, { once: true });

    function waitForKonva() {
        if (konvaHere()) { app.vendorReady.konva = true; return; }
        const wait = window.__whenGlobal
            ? window.__whenGlobal('Konva', KONVA_WAIT_MS)
            : Promise.reject(Object.assign(new Error('Konva did not load'), { code: 'DEP_MISSING', dep: 'Konva' }));
        wait.then(() => {
            if (!konvaHere()) { konvaMissing(new Error('Konva is incomplete')); return; }
            try { if (window.__ae) window.__ae('feature_use', { slug: 'photo-markup', f: 'late_module_boot' }); } catch (_) {}
            konvaArrived();
        }, konvaMissing);
    }
    function konvaArrived() {
        if (!konvaHere()) return;
        app.vendorReady.konva = true;
        app.konvaMissing = false;
        bootNote(null);
        // a late copy that cannot build a stage leaves the explanation, not a rejection
        try { mountStage(); }
        catch (e) { console.error('[photo-markup] stage', e); trackError(e, 'stage_build'); bootNote('failed'); }
    }
    function konvaMissing(err) {
        if (konvaHere()) { konvaArrived(); return; }
        app.konvaMissing = true;
        try {
            if (window.__ae) window.__ae('tool_error', {
                slug: 'photo-markup', error_type: 'module_missing',
                reason: String((err && (err.dep || err.message)) || '').slice(0, 60),
            });
        } catch (_) {}
        bootNote('failed');
        // The wait is bounded, the page is not: a copy that arrives after it
        // still brings the editor up instead of leaving it dead for good.
        const late = () => {
            if (!konvaHere()) return;
            document.removeEventListener('timbrica:script-recovered', late);
            window.removeEventListener('load', late);
            try { if (window.__ae) window.__ae('feature_use', { slug: 'photo-markup', f: 'late_module_boot', after_wait: 1 }); } catch (_) {}
            konvaArrived();
        };
        document.addEventListener('timbrica:script-recovered', late);
        window.addEventListener('load', late);
    }

    // The editor appears the moment a picture opens. Until its stage exists it
    // must not pretend to work: `inert` takes it out of reach (pointer, keyboard,
    // focus) and aria-busy says why to assistive technology.
    function setEditorBusy(on) {
        const ws = $('pmWorkspace');
        if (!ws) return;
        if (on) { ws.setAttribute('inert', ''); ws.setAttribute('aria-busy', 'true'); }
        else { ws.removeAttribute('inert'); ws.removeAttribute('aria-busy'); }
    }
    // The line above the editor: 'loading' while Konva is on its way, 'failed'
    // once the wait is over (it stays until Konva arrives after all), null — gone.
    function bootNote(state) {
        const box = $('pmBootNote');
        if (!box) return;
        box.hidden = !state;
        if (!state) return;
        const failed = state === 'failed';
        box.classList.toggle('tu-boot-fail', failed);
        box.setAttribute('role', failed ? 'alert' : 'status');
        const wait = $('pmBootWait'), fail = $('pmBootFail'), retry = $('pmBootRetry');
        if (wait) wait.hidden = failed;
        if (fail) fail.hidden = !failed;
        if (retry) retry.hidden = !failed;
    }
    // Keys and presses mean something only to a live editor: a stage, on screen,
    // not waiting for its engine.
    function editorLive() {
        const ws = $('pmWorkspace');
        return !!(app.konva.stage && ws && !ws.hidden && !ws.hasAttribute('inert'));
    }

    // ────────────── Upload / image ingest ──────────────
    const MAX_DIM = 4096; // hard render cap; full-res still available for export

    async function loadBlobAsImage(blob) {
        const url = URL.createObjectURL(blob);
        // URL is retained by the Image until it's disposed with the workspace;
        // not revoking is intentional — the same URL is referenced by Konva.Image.
        return await new Promise((resolve, reject) => {
            const i = new Image();
            i.onload = () => resolve(i);
            i.onerror = () => { URL.revokeObjectURL(url); reject(new Error(I18N.errLoad || 'load fail')); };
            i.src = url;
        });
    }

    async function openImageBlob(blob, sourceName) {
        try {
            const img = await loadBlobAsImage(blob);
            // The daily unit is THIS photo, stamped once, here. Anything derived
            // later (a re-encode, a crop, a rotate) still bills to the same key,
            // so a second export of the same picture is free — which is exactly
            // what the wall promises in every locale.
            app.state.limitKey = [
                (blob && blob.name) || sourceName || 'image',
                (blob && blob.size) || 0,
                (blob && blob.lastModified) || 0,
            ].join('|');
            const sizeMB = Math.round(blob.size / 1048576 * 10) / 10;
            // Optional downscale for perf
            if (img.naturalWidth > MAX_DIM || img.naturalHeight > MAX_DIM) {
                trackFeature('downscale_prompt', { w: img.naturalWidth, h: img.naturalHeight });
                const ok = await askDownscale(img.naturalWidth, img.naturalHeight);
                if (ok) {
                    trackFeature('downscale_accepted', { w: img.naturalWidth, h: img.naturalHeight });
                    const scale = MAX_DIM / Math.max(img.naturalWidth, img.naturalHeight);
                    const c = document.createElement('canvas');
                    c.width = Math.round(img.naturalWidth * scale);
                    c.height = Math.round(img.naturalHeight * scale);
                    c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
                    const img2 = new Image();
                    img2.src = c.toDataURL('image/png');
                    await new Promise(r => img2.onload = r);
                    setupWorkspace(img2, sourceName || 'image');
                    trackFeature('upload_ok', { w: img2.naturalWidth, h: img2.naturalHeight, mb: sizeMB, type: blob.type || 'image/*', src: sourceName, downscaled: 1 });
                    return;
                }
            }
            setupWorkspace(img, sourceName || 'image');
            trackFeature('upload_ok', { w: img.naturalWidth, h: img.naturalHeight, mb: sizeMB, type: blob.type || 'image/*', src: sourceName });
        } catch (e) {
            console.error('openImageBlob', e);
            toast(I18N.errLoad || 'Could not load image');
            trackError(e, 'upload', { src: sourceName, type: blob.type, mb: Math.round(blob.size / 1048576 * 10) / 10 });
        }
    }

    function askDownscale(w, h) {
        return new Promise((resolve) => {
            const modal = $('pmDownscaleModal');
            const titleEl = modal.querySelector('[data-fmt="downscale_prompt"]');
            titleEl.textContent = fmt(I18N.downscalePrompt, { w, h });
            modal.hidden = false;
            const yes = $('pmDownscaleYes'), no = $('pmDownscaleNo');
            const cleanup = (v) => {
                modal.hidden = true;
                yes.removeEventListener('click', onYes);
                no.removeEventListener('click', onNo);
                resolve(v);
            };
            const onYes = () => cleanup(true);
            const onNo = () => cleanup(false);
            yes.addEventListener('click', onYes);
            no.addEventListener('click', onNo);
        });
    }

    function initUpload() {
        // <x-tool-uploader> dispatches 'tool-uploader:file'
        const up = $('pmUpload');
        if (up) {
            up.addEventListener('tool-uploader:file', (e) => {
                const file = e.detail.file;
                if (!file) return;
                if (/\.heic$|\.heif$/i.test(file.name) || /heic|heif/i.test(file.type)) {
                    // iPhone HEIC/HEIF: decode to JPEG via heic-to instead of relying
                    // on native <img> support (only Safari has it).
                    if (window.HeicDecode) {
                        window.HeicDecode.normalize(file, window.__ae || null)
                            .then((jpg) => openImageBlob(jpg, jpg.name))
                            .catch(() => toast(I18N.errHeic || 'HEIC not supported'));
                    } else {
                        toast(I18N.errHeic || 'HEIC not supported');
                    }
                    return;
                }
                if (file.type === 'application/pdf') {
                    loadPdfPageOne(file);
                    return;
                }
                openImageBlob(file, file.name);
            });
        }

        // Full-page paste (works on upload + workspace)
        window.addEventListener('paste', (e) => {
            const items = (e.clipboardData && e.clipboardData.items) || [];
            for (const it of items) {
                if (it.type && it.type.startsWith('image/')) {
                    const blob = it.getAsFile();
                    if (blob) { openImageBlob(blob, 'pasted.' + (it.type.split('/')[1] || 'png')); e.preventDefault(); return; }
                }
            }
        });

        // Full-page drag-drop fallback (outside upload screen too)
        ['dragover','drop'].forEach(evt => window.addEventListener(evt, (e) => {
            if (!e.dataTransfer) return;
            const types = e.dataTransfer.types || [];
            if (!types.includes('Files')) return;
            e.preventDefault();
            if (evt === 'drop') {
                const file = e.dataTransfer.files[0];
                if (file && file.type.startsWith('image/')) openImageBlob(file, file.name);
            }
        }));

        // Open new image button
        $('pmOpenNew')?.addEventListener('click', () => {
            $('pmUploadScreen').hidden = false;
            const ws = $('pmWorkspace');
            ws.hidden = true;
            ws.classList.remove('active');
            document.body.classList.remove('pm-workspace-active');
        });

    }

    // PDF first page → canvas (lazy pdf.js with fallback)
    async function loadPdfPageOne(file) {
        toast(I18N.pdfLoading || 'Loading PDF…');
        trackFeature('pdf_import_start');
        const sources = [
            { lib: '/vendor/pdfjs/4/pdf.min.mjs', wrk: '/vendor/pdfjs/4/pdf.worker.min.mjs' },
            { lib: '/cdn/jsdelivr/npm/pdfjs-dist@4.4.168/build/pdf.min.mjs', wrk: '/cdn/jsdelivr/npm/pdfjs-dist@4.4.168/build/pdf.worker.min.mjs' },
        ];
        let pdfjsLib, workerSrc;
        for (const s of sources) {
            try {
                pdfjsLib = await import(s.lib);
                workerSrc = s.wrk;
                break;
            } catch (e) { console.warn('[photo-markup] pdf.js import failed:', s.lib); }
        }
        if (!pdfjsLib) { toast('PDF load failed'); trackError(new Error('pdfjs unavailable'), 'pdf_import'); return; }
        try {
            pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc;
            const buf = await file.arrayBuffer();
            const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
            const page = await pdf.getPage(1);
            const viewport = page.getViewport({ scale: 2 });
            const cv = document.createElement('canvas');
            cv.width = viewport.width; cv.height = viewport.height;
            await page.render({ canvasContext: cv.getContext('2d'), viewport }).promise;
            cv.toBlob(b => openImageBlob(b, (file.name || 'doc').replace(/\.pdf$/i, '') + '-page1.png'), 'image/png');
            trackFeature('pdf_import_ok', { pages: pdf.numPages });
        } catch (err) {
            console.error(err);
            toast('PDF load failed');
            trackError(err, 'pdf_render');
        }
    }

    // ────────────── Workspace setup ──────────────
    // `after` runs once the stage of THIS picture exists (a recovered session
    // puts its marks back there).
    function setupWorkspace(image, name, after) {
        $('pmUploadScreen').hidden = true;
        const ws = $('pmWorkspace');
        ws.hidden = false;
        // Workspace must have .active class — global Timbrica CSS disables pointer-events on inactive workspaces
        ws.classList.add('active');
        // Collapse intro (tool-intro / tool-h2 / tool-ad) so canvas is above the fold
        document.body.classList.add('pm-workspace-active');
        // Layout listens for this to start the tool-processing telemetry timer
        window.dispatchEvent(new CustomEvent('workspace-activated'));

        // Session telemetry: stamp opened-at; exit summary fires from beforeunload/pagehide
        app.session = {
            openedAt: performance.now(),
            source: name || 'image',
            w: image.naturalWidth || image.width,
            h: image.naturalHeight || image.height,
        };
        trackFeature('workspace_open', { src: name || 'image', w: app.session.w, h: app.session.h });

        // The stage is built for the picture opened LAST: one picked while Konva
        // is still on its way replaces the waiting one before anything is built.
        app.pendingStage = { image, name: name || 'image', after: after || null };
        if (konvaHere()) { mountStage(); return; }
        setEditorBusy(true);
        bootNote(app.konvaMissing ? 'failed' : 'loading');
    }

    function mountStage() {
        const job = app.pendingStage;
        if (!job || !konvaHere()) return;
        app.pendingStage = null;
        buildStage(job.image);
        bindCanvasEvents();
        refreshLayerPanel();
        // A new picture starts its own history: the previous picture's steps
        // stayed in the stack, and Ctrl+Z right after opening put its marks here.
        app.history.stack = [];
        app.history.ptr = -1;
        pushHistory();
        // A fresh picture starts on the Arrow — the annotation convention
        // (CleanShot, Annotely, Markup Hero). Starting on Select made the
        // first drag do nothing and the hint talk about marks that did not
        // exist. Decided HERE, once the new layer exists: an earlier call
        // read the previous picture's layer (or none) and ran before
        // app.session was set.
        if (!app.konva.drawLayer.find('.pm-obj').length) selectTool('arrow');
        else selectTool(app.state.tool || 'select');
        setEditorBusy(false);
        bootNote(null);
        if (job.after) { try { job.after(); } catch (e) { console.warn('[photo-markup] after stage', e); } }
        // tools-extra.js lays the sample's marks on this — the editor was
        // revealed long before there was a layer to draw on when Konva was late.
        window.dispatchEvent(new CustomEvent('pm:stage-ready', { detail: { name: job.name } }));
    }

    // Session-exit summary (annotation count, time spent). Uses pagehide
    // because Safari/iOS does not reliably fire beforeunload on tab close.
    let _sessionExitSent = false;
    function sendSessionExit(reason) {
        if (_sessionExitSent || !app.session) return;
        _sessionExitSent = true;
        try {
            const annot = app.konva.drawLayer ? app.konva.drawLayer.find('.pm-obj').length : 0;
            const ms = Math.round(performance.now() - app.session.openedAt);
            trackFeature('workspace_exit', { reason: reason || 'pagehide', ms, annot, w: app.session.w, h: app.session.h });
        } catch(_) {}
    }
    window.addEventListener('pagehide', () => sendSessionExit('pagehide'));
    window.addEventListener('beforeunload', () => sendSessionExit('beforeunload'));

    function buildStage(image) {
        const container = $('pmStage');
        // Konva keeps every stage in its own list (Konva.stages) until destroy():
        // emptying the container left each previous picture's layers, hit
        // canvases and buffers alive for the life of the page.
        if (app.konva.stage) { try { app.konva.stage.destroy(); } catch (_) {} }
        container.innerHTML = '';
        app.state.imgW = image.naturalWidth || image.width;
        app.state.imgH = image.naturalHeight || image.height;
        app.state.imageSource = image;

        const stage = new Konva.Stage({
            container: 'pmStage',
            width: app.state.imgW,
            height: app.state.imgH,
        });

        const bgLayer = new Konva.Layer({ listening: false });
        const imgNode = new Konva.Image({ image, x: 0, y: 0, width: app.state.imgW, height: app.state.imgH });
        bgLayer.add(imgNode);
        stage.add(bgLayer);

        const drawLayer = new Konva.Layer();
        stage.add(drawLayer);

        const overlayLayer = new Konva.Layer();
        stage.add(overlayLayer);

        const tr = new Konva.Transformer({
            rotateEnabled: true,
            anchorCornerRadius: 4,
            anchorStroke: '#7aa7ff',
            anchorFill: '#fff',
            borderStroke: '#7aa7ff',
            padding: 2,
            ignoreStroke: true,
        });
        overlayLayer.add(tr);

        app.konva.stage = stage;
        app.konva.bgLayer = bgLayer;
        app.konva.imageNode = imgNode;
        app.konva.drawLayer = drawLayer;
        app.konva.overlayLayer = overlayLayer;
        app.konva.transformer = tr;

        fitToView();
    }

    function fitToView() {
        const scroll = $('pmCanvasScroll');
        const rect = scroll.getBoundingClientRect();
        const padding = 56;
        const sx = (rect.width - padding) / app.state.imgW;
        const sy = (rect.height - padding) / app.state.imgH;
        const scale = Math.min(sx, sy, 1);
        setZoom(scale);
    }

    /* The stage IS the canvas: zoom multiplies its pixel size, and three layers
       (plus Konva's hit canvas) are allocated at that size. A 4096-px image at
       800 % is a 32 768-px canvas — past Firefox's per-side limit (32 767) and
       far past its area limit, so the backing store fails to allocate and the
       next pointermove died inside Konva's hit test with NS_ERROR_FAILURE
       (Sentry M9, Firefox 144). The ceiling below keeps every layer under a
       memory budget on the device it runs on. */
    function maxZoomFor(w, h) {
        const dpr = Math.max(1, Math.min(3, (window.devicePixelRatio || 1)));
        const sideCap = 16384 / dpr;                              // GPU texture / canvas side
        // the memory number has one owner on the site: window.__memBudget (a constrained
        // iOS tab answers a low ceiling whatever the RAM is)
        const B = window.__memBudget;
        const gb = (B && typeof B.gb === 'function') ? B.gb() : 4;
        const tight = (B && typeof B.constrained === 'function' && B.constrained()) || gb < 8;
        const areaCap = (tight ? 40e6 : 96e6) / (dpr * dpr);                       // px per layer
        return Math.max(0.1, Math.min(8, sideCap / Math.max(w, h), Math.sqrt(areaCap / Math.max(1, w * h))));
    }
    function setZoom(scale) {
        const s = app.konva.stage;
        // No stage, nothing to scale: a zoom press before Konva read the width
        // of null (Sentry T3). The editor is inert then; this is the backstop.
        if (!s) return;
        scale = Math.max(0.1, Math.min(maxZoomFor(app.state.imgW, app.state.imgH), scale));
        app.state.zoom = scale;
        s.width(app.state.imgW * scale);
        s.height(app.state.imgH * scale);
        s.scale({ x: scale, y: scale });
        s.batchDraw();
        $('pmZoomReadout').textContent = Math.round(scale * 100) + '%';
    }

    // ────────────── Canvas events ──────────────
    function bindCanvasEvents() {
        const stage = app.konva.stage;
        const area = $('pmCanvasArea');

        stage.on('pointerdown', onPointerDown);
        stage.on('pointermove', onPointerMove);
        stage.on('pointerup', onPointerUp);

        // Zoom with wheel (Ctrl+wheel = zoom, plain wheel lets the scroll container scroll).
        // The area outlives the stage: bound once, or every picture opened added
        // one more listener and a notch zoomed by 8 % once per picture.
        if (!area.__pmWheel) {
            area.__pmWheel = true;
            area.addEventListener('wheel', (e) => {
                if (!e.ctrlKey && !e.metaKey) return;
                e.preventDefault();
                const dir = e.deltaY > 0 ? -1 : 1;
                setZoom(app.state.zoom * (1 + dir * 0.08));
            }, { passive: false });
        }

        stage.on('click tap', (e) => {
            if (app.state.tool !== 'select') return;
            const tr = app.konva.transformer;
            const shift = e.evt && (e.evt.shiftKey || e.evt.metaKey || e.evt.ctrlKey);
            if (e.target === stage || e.target === app.konva.imageNode) {
                if (!shift) tr.nodes([]);
                return;
            }
            if (!e.target.hasName('pm-obj')) return;
            // Don't select locked nodes
            if (e.target.getAttr('pmLocked')) return;
            const cur = tr.nodes();
            if (shift) {
                const ix = cur.indexOf(e.target);
                if (ix >= 0) tr.nodes(cur.filter(n => n !== e.target)); // toggle off
                else tr.nodes(cur.concat([e.target]));                 // add
            } else {
                tr.nodes([e.target]);
            }
            app.konva.overlayLayer.batchDraw();
        });
    }

    function stagePointer() {
        const p = app.konva.stage.getPointerPosition();
        const s = app.konva.stage.scaleX() || 1;
        return { x: p.x / s, y: p.y / s };
    }
    Object.assign(app.api, {
        // late-bound: autosave WRAPS pushHistory further down; a plain reference
        // here kept the unwrapped one, and nothing tools-extra.js adds (stickers,
        // callouts, measures, magnifier) reached the saved session
        pushHistory: (...a) => pushHistory(...a),
        stagePointer, refreshLayerPanel, toast, trackFeature,
        selectTool, rememberColor, highlightPaletteSwatch,
        setCompareMode: (v) => setCompareMode(v),
        addNumberedStep: (p) => addNumberedStep(p),
        enterTextEdit: (t, cb) => enterTextEdit(t, cb),
        applyAreaFilter: (kind, bbox) => applyAreaFilter(kind, bbox),
    });

    function onPointerDown(e) {
        if (app.ui.spaceHeld) { startPan(); return; }
        const t = app.state.tool;
        if (t === 'select') return;

        if (e.evt && e.evt.button === 2) return; // ignore right-click
        // Extension point. Tools that live in tools-extra.js (callout, magnifier,
        // measure, sticker, the solid-fill finish of "hide") claim the gesture
        // here and return true; everything below is the built-in set.
        if (window.PMX && PMX.onDown && PMX.onDown(t, e) === true) return;
        if (e.target !== app.konva.stage && e.target !== app.konva.imageNode && t !== 'pen' && t !== 'highlighter' && t !== 'eraser') return;

        const p = stagePointer();
        app.ui.drawing = { type: t, start: p, points: [p], node: null, pressure: e.evt?.pressure ?? 0.5 };

        const common = { name: 'pm-obj', draggable: false };
        const sc = app.state.color;
        const sw = app.state.stroke;
        const op = app.state.opacity;

        if (t === 'arrow') {
            const arr = new Konva.Arrow({
                ...common, points: [p.x, p.y, p.x, p.y],
                pointerLength: Math.max(10, sw * 2.5),
                pointerWidth: Math.max(10, sw * 2.5),
                pointerAtBeginning: app.state.arrowStyle === 'double',
                stroke: sc, fill: sc, strokeWidth: sw, opacity: op, hitStrokeWidth: Math.max(sw, 14),
                dash: app.state.arrowDashed ? [sw * 3, sw * 2] : undefined,
            });
            app.konva.drawLayer.add(arr);
            app.ui.drawing.node = arr;
        } else if (t === 'line') {
            const ln = new Konva.Line({
                ...common, points: [p.x, p.y, p.x, p.y],
                stroke: sc, strokeWidth: sw, opacity: op, lineCap: 'round', hitStrokeWidth: Math.max(sw, 14),
                dash: app.state.arrowDashed ? [sw * 3, sw * 2] : undefined,
            });
            app.konva.drawLayer.add(ln);
            app.ui.drawing.node = ln;
        } else if (t === 'rect') {
            const r = new Konva.Rect({
                ...common, x: p.x, y: p.y, width: 0, height: 0,
                stroke: sc, strokeWidth: sw, opacity: op,
                fill: app.state.shapeFill ? sc : 'transparent',
                cornerRadius: 4, hitStrokeWidth: Math.max(sw, 14),
            });
            app.konva.drawLayer.add(r);
            app.ui.drawing.node = r;
        } else if (t === 'ellipse') {
            const el = new Konva.Ellipse({
                ...common, x: p.x, y: p.y, radiusX: 0, radiusY: 0,
                stroke: sc, strokeWidth: sw, opacity: op,
                fill: app.state.shapeFill ? sc : 'transparent', hitStrokeWidth: Math.max(sw, 14),
            });
            app.konva.drawLayer.add(el);
            app.ui.drawing.node = el;
        } else if (t === 'pen') {
            app.ui.drawing.rawPoints = [[p.x, p.y, app.ui.drawing.pressure]];
            // perfect-freehand shapes the stroke. A stroke begun before it is here
            // (or where it never comes) is a plain round line of the same weight;
            // the first stroke after it lands is its own again.
            const pathNode = pfReady()
                ? new Konva.Path({ ...common, data: '', fill: sc, opacity: op })
                : new Konva.Line({
                    ...common, points: [p.x, p.y, p.x, p.y], pmKind: 'pen',
                    stroke: sc, strokeWidth: Math.max(2, sw * 1.5), opacity: op,
                    lineCap: 'round', lineJoin: 'round', hitStrokeWidth: Math.max(sw * 1.5, 14),
                });
            app.konva.drawLayer.add(pathNode);
            app.ui.drawing.node = pathNode;
        } else if (t === 'highlighter') {
            const hl = new Konva.Line({
                ...common, points: [p.x, p.y],
                stroke: sc, strokeWidth: Math.max(sw * 3, 14),
                opacity: Math.min(0.35, op * 0.35),
                lineCap: 'round', lineJoin: 'round', tension: 0.4,
                globalCompositeOperation: 'source-over',
            });
            app.konva.drawLayer.add(hl);
            app.ui.drawing.node = hl;
        } else if (t === 'text') {
            finishDrawing(false);
            addText(p);
            return;
        } else if (t === 'number') {
            finishDrawing(false);
            addNumberedStep(p);
            pushHistory();
            return;
        } else if (t === 'blur' || t === 'pixelate') {
            // placeholder for blur-area rect (actual filter applied on mouseup)
            const r = new Konva.Rect({
                ...common, x: p.x, y: p.y, width: 0, height: 0,
                stroke: t === 'blur' ? '#60a5fa' : '#a78bfa', strokeWidth: 2,
                dash: [6, 4], fill: 'rgba(96,165,250,.08)',
            });
            app.konva.drawLayer.add(r);
            app.ui.drawing.node = r;
        } else if (t === 'spotlight') {
            // Draw a bbox preview on overlayLayer; finalize as spotlight group on pointerup
            const r = new Konva.Rect({
                x: p.x, y: p.y, width: 0, height: 0,
                stroke: '#ffd54f', strokeWidth: 2, dash: [6, 4],
                fill: 'rgba(255,213,79,.1)', listening: false,
            });
            app.konva.overlayLayer.add(r);
            app.ui.drawing.node = r;
        } else if (t === 'eraser') {
            // click-to-erase on pointerdown
            if (e.target !== app.konva.stage && e.target !== app.konva.imageNode && e.target.hasName('pm-obj')) {
                e.target.destroy();
                app.konva.drawLayer.batchDraw();
                pushHistory();
                refreshLayerPanel();
            }
            app.ui.drawing = null;
            return;
        } else if (t === 'eyedropper') {
            const color = pickColorAt(p);
            if (color) {
                app.state.color = color;
                $('pmColor').value = color;
                rememberColor(color);
                highlightPaletteSwatch(color);
                toast((I18N.colorPicked || 'Color picked') + ': ' + color);
                trackFeature('eyedropper_pick', { color });
            }
            app.ui.drawing = null;
            selectTool('select'); // snap back to select after picking
            return;
        }

        app.konva.drawLayer.batchDraw();
    }

    function onPointerMove(e) {
        if (app.ui.panStart) { doPan(); return; }
        const d = app.ui.drawing;
        if (!d || !d.node) return;
        if (window.PMX && PMX.onMove && PMX.onMove(d, e) === true) return;

        const p = stagePointer();
        const shift = e.evt && e.evt.shiftKey;
        const t = d.type;

        if (t === 'arrow' || t === 'line') {
            let ex = p.x, ey = p.y;
            if (shift) {
                const dx = p.x - d.start.x, dy = p.y - d.start.y;
                const angle = Math.atan2(dy, dx);
                const step = Math.PI / 4;
                const snapped = Math.round(angle / step) * step;
                const r = Math.hypot(dx, dy);
                ex = d.start.x + Math.cos(snapped) * r;
                ey = d.start.y + Math.sin(snapped) * r;
            }
            d.node.points([d.start.x, d.start.y, ex, ey]);
        } else if (t === 'rect') {
            let w = p.x - d.start.x, h = p.y - d.start.y;
            if (shift) { const s = Math.max(Math.abs(w), Math.abs(h)); w = Math.sign(w) * s || s; h = Math.sign(h) * s || s; }
            if (w < 0) { d.node.x(d.start.x + w); d.node.width(-w); } else { d.node.x(d.start.x); d.node.width(w); }
            if (h < 0) { d.node.y(d.start.y + h); d.node.height(-h); } else { d.node.y(d.start.y); d.node.height(h); }
        } else if (t === 'ellipse') {
            let rx = Math.abs(p.x - d.start.x), ry = Math.abs(p.y - d.start.y);
            if (shift) rx = ry = Math.max(rx, ry);
            d.node.x((d.start.x + p.x) / 2);
            d.node.y((d.start.y + p.y) / 2);
            d.node.radiusX(rx / 2 * 2);
            d.node.radiusY(ry / 2 * 2);
        } else if (t === 'pen') {
            const pr = e.evt?.pressure ?? 0.5;
            d.rawPoints.push([p.x, p.y, pr || 0.5]);
            updatePenPath(d);
        } else if (t === 'highlighter') {
            const pts = d.node.points();
            pts.push(p.x, p.y);
            d.node.points(pts);
        } else if (t === 'blur' || t === 'pixelate') {
            let w = p.x - d.start.x, h = p.y - d.start.y;
            if (w < 0) { d.node.x(d.start.x + w); d.node.width(-w); } else { d.node.x(d.start.x); d.node.width(w); }
            if (h < 0) { d.node.y(d.start.y + h); d.node.height(-h); } else { d.node.y(d.start.y); d.node.height(h); }
        } else if (t === 'spotlight') {
            let w = p.x - d.start.x, h = p.y - d.start.y;
            if (shift) { const s = Math.max(Math.abs(w), Math.abs(h)); w = Math.sign(w) * s || s; h = Math.sign(h) * s || s; }
            if (w < 0) { d.node.x(d.start.x + w); d.node.width(-w); } else { d.node.x(d.start.x); d.node.width(w); }
            if (h < 0) { d.node.y(d.start.y + h); d.node.height(-h); } else { d.node.y(d.start.y); d.node.height(h); }
        }

        app.konva.drawLayer.batchDraw();
        if (d.type === 'spotlight') app.konva.overlayLayer.batchDraw();
    }

    function updatePenPath(d) {
        if (d.node.getClassName() === 'Line') {
            // the plain stroke (perfect-freehand was not here when it began)
            const last = d.rawPoints[d.rawPoints.length - 1];
            d.node.points(d.node.points().concat([last[0], last[1]]));
            return;
        }
        try {
            const stroke = window.PerfectFreehand.getStroke(d.rawPoints, {
                size: Math.max(2, app.state.stroke * 2),
                thinning: 0.5,
                smoothing: 0.5,
                streamline: 0.5,
                simulatePressure: d.rawPoints.every(p => p[2] === 0.5),
            });
            if (!stroke || stroke.length < 2) return;
            const data = pathDataFromStroke(stroke);
            d.node.data(data);
        } catch (err) { console.warn('pen update', err); }
    }

    function pathDataFromStroke(points) {
        if (!points.length) return '';
        let d = 'M ' + points[0][0].toFixed(2) + ' ' + points[0][1].toFixed(2);
        for (let i = 1; i < points.length; i++) d += ' L ' + points[i][0].toFixed(2) + ' ' + points[i][1].toFixed(2);
        d += ' Z';
        return d;
    }

    function onPointerUp() {
        if (app.ui.panStart) { endPan(); return; }
        finishDrawing(true);
    }

    function finishDrawing(shouldPush) {
        const d = app.ui.drawing;
        if (!d) return;
        app.ui.drawing = null;

        if (!d.node) return;
        if (window.PMX && PMX.onUp && PMX.onUp(d, shouldPush) === true) return;

        // Remove zero-size shapes (accidental clicks)
        if (d.type === 'rect' || d.type === 'blur' || d.type === 'pixelate') {
            if (Math.abs(d.node.width()) < 4 || Math.abs(d.node.height()) < 4) {
                d.node.destroy();
                app.konva.drawLayer.batchDraw();
                return;
            }
        }
        if (d.type === 'arrow' || d.type === 'line') {
            const p = d.node.points();
            if (Math.hypot(p[2] - p[0], p[3] - p[1]) < 3) {
                d.node.destroy();
                app.konva.drawLayer.batchDraw();
                return;
            }
        }
        if (d.type === 'ellipse') {
            if (d.node.radiusX() < 2 || d.node.radiusY() < 2) {
                d.node.destroy();
                app.konva.drawLayer.batchDraw();
                return;
            }
        }

        // Finalize blur/pixelate: swap placeholder for actual bitmap region with Konva filter
        if (d.type === 'blur' || d.type === 'pixelate') {
            const p = d.node;
            const bbox = { x: p.x(), y: p.y(), w: p.width(), h: p.height() };
            p.destroy();
            applyAreaFilter(d.type, bbox);
        } else if (d.type === 'spotlight') {
            const p = d.node;
            const bbox = { x: p.x(), y: p.y(), w: p.width(), h: p.height() };
            p.destroy();
            if (bbox.w < 8 || bbox.h < 8) {
                app.konva.overlayLayer.batchDraw();
                return;
            }
            createSpotlight(bbox);
            app.konva.overlayLayer.batchDraw();
            if (shouldPush) pushHistory();
            refreshLayerPanel();
            return;
        } else {
            d.node.draggable(true);
            if (d.type === 'pen' && d.rawPoints) {
                // Attempt shape recognition — replaces pen stroke with perfect shape if rough circle/rect/line
                if (maybeRecognize(d)) {
                    d.node = null; // recognized path, already replaced
                } else {
                    d.node.setAttr('pmRawPoints', d.rawPoints);
                }
            }
        }

        app.konva.drawLayer.batchDraw();
        if (shouldPush) pushHistory();
        refreshLayerPanel();
    }

    function applyAreaFilter(kind, bbox) {
        const stage = app.konva.stage;
        const x = Math.max(0, bbox.x), y = Math.max(0, bbox.y);
        const w = Math.min(app.state.imgW - x, bbox.w), h = Math.min(app.state.imgH - y, bbox.h);
        if (w <= 0 || h <= 0) return;

        const src = app.konva.imageNode.image();
        const c = document.createElement('canvas');
        c.width = w; c.height = h;
        const ctx = c.getContext('2d');
        ctx.drawImage(src, x, y, w, h, 0, 0, w, h);

        const node = new Konva.Image({
            name: 'pm-obj',
            x, y, image: c, width: w, height: h, draggable: true,
        });
        node.setAttr('pmFilterKind', kind);
        node.setAttr('pmFilterParam', kind === 'blur' ? app.state.blurRadius : app.state.pixelSize);

        node.cache();
        if (kind === 'blur') {
            node.filters([Konva.Filters.Blur]);
            node.blurRadius(app.state.blurRadius);
        } else {
            node.filters([Konva.Filters.Pixelate]);
            node.pixelSize(app.state.pixelSize);
        }
        app.konva.drawLayer.add(node);
        app.konva.drawLayer.batchDraw();
        pushHistory();
        refreshLayerPanel();
    }

    // ────────────── Text tool ──────────────
    function addText(pos) {
        const t = new Konva.Text({
            name: 'pm-obj',
            x: pos.x, y: pos.y,
            text: 'Text',
            fontSize: app.state.fontSize,
            fontFamily: app.state.fontFamily,
            fill: app.state.color,
            opacity: app.state.opacity,
            draggable: true,
            padding: app.state.textBg ? 8 : 0,
        });
        if (app.state.textBg) {
            t.setAttr('pmBg', true);
        }
        if (app.state.textOutline) {
            t.stroke(app.state.outlineColor || '#000');
            t.strokeWidth(2);
            t.setAttr('pmOutline', true);
        }
        app.konva.drawLayer.add(t);
        if (app.state.textBg) addTextBgUpdater(t);
        app.konva.drawLayer.batchDraw();

        // Immediately open edit prompt
        enterTextEdit(t);
    }

    function addTextBgUpdater(t) {
        // Draw BG rect behind text
        const bg = new Konva.Rect({
            name: 'pm-text-bg',
            fill: '#000', opacity: 0.65, cornerRadius: 4, listening: false,
        });
        t.setAttr('pmBgNode', bg);
        bg.moveToTop();
        t.moveToTop();
        app.konva.drawLayer.add(bg);
        const sync = () => {
            const x = t.x() - 4, y = t.y() - 2;
            bg.position({ x, y });
            bg.size({ width: t.width() + 8, height: t.height() + 4 });
            bg.moveToBottom();
            t.moveToTop();
        };
        t.on('xChange yChange widthChange heightChange textChange fontSizeChange', sync);
        sync();
    }

    function enterTextEdit(t, onCommit) {
        if (app.ui.editingText) return;
        app.ui.editingText = t;
        const stage = app.konva.stage;
        const areaPos = stage.container().getBoundingClientRect();
        const scale = app.state.zoom;
        const abs = t.getAbsolutePosition();
        const ta = document.createElement('textarea');
        ta.value = t.text();
        // iOS Safari auto-zooms inputs <16px — force minimum 16px for editor textarea
        const editorFontPx = Math.max(16, t.fontSize() * scale);
        // White label on a filled callout needs a dark editor, not a white one.
        const fillHex = String(t.fill() || '#000').replace('#', '');
        const fill6 = fillHex.length === 3 ? fillHex.split('').map(c => c + c).join('') : fillHex;
        const lum = fill6.length === 6 ? (0.2126 * parseInt(fill6.slice(0, 2), 16) + 0.7152 * parseInt(fill6.slice(2, 4), 16) + 0.0722 * parseInt(fill6.slice(4, 6), 16)) : 0;
        Object.assign(ta.style, {
            position: 'fixed',
            top: (areaPos.top + abs.y) + 'px',
            left: (areaPos.left + abs.x) + 'px',
            fontSize: editorFontPx + 'px',
            fontFamily: t.fontFamily(),
            color: t.fill(),
            background: lum > 150 ? 'rgba(30,41,59,.96)' : 'rgba(255,255,255,.96)',
            border: '2px solid #7aa7ff',
            borderRadius: '6px',
            padding: '4px 6px',
            outline: 'none',
            resize: 'none',
            zIndex: 1002,
            minWidth: '60px',
            minHeight: '30px',
            lineHeight: '1.25',
        });
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        const commit = () => {
            const v = ta.value.trim();
            if (!v) t.destroy();
            else t.text(v);
            if (onCommit) { try { onCommit(v); } catch (err) { console.warn('text commit hook', err); } }
            app.konva.drawLayer.batchDraw();
            ta.remove();
            app.ui.editingText = null;
            pushHistory();
            refreshLayerPanel();
        };
        ta.addEventListener('blur', commit, { once: true });
        ta.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') { ta.value = t.text() || ''; ta.blur(); }
            // Shift+Enter = newline (native default); plain Enter = commit
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ta.blur(); }
        });
        // Auto-grow textarea height to fit multi-line content
        const autoGrow = () => {
            ta.style.height = 'auto';
            ta.style.height = Math.min(400, ta.scrollHeight) + 'px';
        };
        ta.addEventListener('input', autoGrow);
        setTimeout(autoGrow, 0);
    }

    // ────────────── Numbered step tool ──────────────
    function addNumberedStep(pos) {
        const n = app.state.counter++;
        const R = 16 + Math.max(0, (app.state.stroke - 4) * 0.8);
        const group = new Konva.Group({ name: 'pm-obj', x: pos.x, y: pos.y, draggable: true });
        group.setAttr('pmNumber', n);
        const circle = new Konva.Circle({
            radius: R, fill: app.state.color, stroke: '#fff', strokeWidth: 2,
            shadowColor: 'rgba(0,0,0,.4)', shadowBlur: 4, shadowOffsetY: 2,
        });
        const label = new Konva.Text({
            text: String(n), fontSize: R * 1.1, fontFamily: 'system-ui, -apple-system, sans-serif',
            fontStyle: 'bold', fill: '#fff', listening: false,
        });
        label.offsetX(label.width() / 2);
        label.offsetY(label.height() / 2);
        group.add(circle);
        group.add(label);
        app.konva.drawLayer.add(group);
        app.konva.drawLayer.batchDraw();
    }

    // ────────────── Pan (space + drag) ──────────────
    function startPan() {
        app.ui.panStart = { x: app.konva.stage.getPointerPosition()?.x, y: app.konva.stage.getPointerPosition()?.y };
        $('pmCanvasArea').classList.add('is-panning');
    }
    function doPan() { /* native scroll already works; no-op placeholder */ }
    function endPan() { app.ui.panStart = null; $('pmCanvasArea').classList.remove('is-panning'); }

    // ────────────── History ──────────────
    function pushHistory() {
        if (app.history.busy) return;
        if (!app.konva.drawLayer) return;
        const snap = serializeLayer();
        const h = app.history;
        h.stack = h.stack.slice(0, h.ptr + 1);
        h.stack.push(snap);
        if (h.stack.length > h.capacity) h.stack.shift();
        h.ptr = h.stack.length - 1;
        updateUndoRedoButtons();
    }

    function serializeLayer() {
        return app.konva.drawLayer.toJSON();
    }

    function restoreLayer(json) {
        app.history.busy = true;
        const stage = app.konva.stage;
        const oldLayer = app.konva.drawLayer;
        const newLayer = Konva.Node.create(json);
        // Re-cache image nodes for filters after restore
        newLayer.find('Image').forEach(n => {
            try { n.cache(); } catch(e) {}
        });
        // Spotlight groups rely on destination-out compositing, which requires
        // an explicit cache after re-create.
        newLayer.find('.pm-spotlight').forEach(n => {
            try { n.cache(); } catch(e) {}
        });
        stage.add(newLayer);
        newLayer.moveUp();
        // Move overlay above new layer
        app.konva.overlayLayer.moveToTop();
        oldLayer.destroy();
        app.konva.drawLayer = newLayer;
        newLayer.batchDraw();
        app.konva.transformer.nodes([]);
        refreshLayerPanel();
        app.history.busy = false;
    }

    function undo() {
        const h = app.history;
        if (h.ptr <= 0) return;
        h.ptr--;
        restoreLayer(h.stack[h.ptr]);
        updateUndoRedoButtons();
        trackFeature('undo');
    }
    function redo() {
        const h = app.history;
        if (h.ptr >= h.stack.length - 1) return;
        h.ptr++;
        restoreLayer(h.stack[h.ptr]);
        updateUndoRedoButtons();
        trackFeature('redo');
    }
    function updateUndoRedoButtons() {
        $('pmUndo').disabled = app.history.ptr <= 0;
        $('pmRedo').disabled = app.history.ptr >= app.history.stack.length - 1;
        // Compare-toggle is only useful once at least one annotation exists.
        const cmp = $('pmCompare');
        if (cmp) {
            const hasAnnots = app.konva.drawLayer && app.konva.drawLayer.find('.pm-obj').length > 0;
            cmp.disabled = !hasAnnots;
            // If annotations were just removed (e.g. via undo), exit compare mode.
            if (!hasAnnots && cmp.getAttribute('aria-pressed') === 'true') {
                setCompareMode(false);
            }
        }
    }

    // ────────────── Compare-toggle (before/after preview) ──────────────
    // Click toggles: hides every .pm-obj annotation + face overlay so the user
    // sees the clean original; click again restores. Telemetry: dedup on first
    // hide per session.
    let _compareUsedSent = false;
    function setCompareMode(on) {
        const btn = $('pmCompare');
        if (!btn) return;
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        if (app.konva.drawLayer) {
            // Don't clobber per-node visibility set by user (lock/hide). Stash
            // the user's visible() state on first hide so restore is exact.
            app.konva.drawLayer.find('.pm-obj').forEach(n => {
                if (on) {
                    n.setAttr('pmCompareSavedVis', n.visible());
                    n.visible(false);
                } else {
                    const saved = n.getAttr('pmCompareSavedVis');
                    n.visible(saved !== false);
                    n.setAttr('pmCompareSavedVis', undefined);
                }
            });
            app.konva.drawLayer.batchDraw();
        }
        // Hide the face-detection overlay rectangles too, if present.
        if (app.konva.overlayLayer) {
            app.konva.overlayLayer.find('.pm-face-box').forEach(n => n.visible(!on));
            app.konva.overlayLayer.batchDraw();
        }
    }
    function toggleCompareMode() {
        const btn = $('pmCompare');
        if (!btn || btn.disabled) return;
        const next = btn.getAttribute('aria-pressed') !== 'true';
        setCompareMode(next);
        if (next && !_compareUsedSent) {
            _compareUsedSent = true;
            const annot = app.konva.drawLayer ? app.konva.drawLayer.find('.pm-obj').length : 0;
            trackFeature('compare_toggle_used', { annot });
        }
    }

    // ────────────── Layer panel ──────────────
    function refreshLayerPanel() {
        const host = $('pmLayers');
        if (!host) return;
        const nodes = app.konva.drawLayer ? app.konva.drawLayer.find('.pm-obj') : [];
        host.innerHTML = '';
        // Original image (fixed)
        const orig = document.createElement('div');
        orig.className = 'pm-layer pm-layer-orig';
        orig.innerHTML = `<span class="pm-layer-icon">🖼️</span><span class="pm-layer-name">${I18N.layerOriginal || 'Original image'}</span>`;
        host.appendChild(orig);

        if (!nodes.length) {
            const empty = document.createElement('div');
            empty.className = 'pm-layer-empty';
            empty.textContent = I18N.layerEmpty || 'No objects yet.';
            host.appendChild(empty);
            return;
        }
        // Reverse so top-most paints first
        nodes.slice().reverse().forEach((n, ix) => {
            const el = document.createElement('div');
            el.className = 'pm-layer';
            if (n.getAttr('pmLocked')) el.classList.add('is-locked');
            if (!n.visible()) el.classList.add('is-hidden');
            const locked = !!n.getAttr('pmLocked');
            const hidden = !n.visible();
            // Build DOM with textContent for user-controlled `name` (Text nodes use n.text())
            const iconSpan = document.createElement('span');
            iconSpan.className = 'pm-layer-icon';
            const ico = friendlyNodeIcon(n);
            // Strip icons are our own SVG markup; everything else is text.
            if (ico.startsWith('<svg')) iconSpan.innerHTML = ico; else iconSpan.textContent = ico;
            const nameSpan = document.createElement('span');
            nameSpan.className = 'pm-layer-name';
            nameSpan.textContent = friendlyNodeName(n);
            const btns = document.createElement('span');
            btns.className = 'pm-layer-btns';
            const mkBtn = (act, title, glyph, on) => {
                const b = document.createElement('button');
                b.type = 'button'; b.dataset.act = act;
                b.title = (I18N.layerActs && I18N.layerActs[act]) || title;
                b.textContent = glyph;
                if (on) b.classList.add('is-on');
                return b;
            };
            const acts = I18N.layerActs || {};
            btns.appendChild(mkBtn('hide', hidden ? (acts.show || 'Show') : (acts.hide || 'Hide'), hidden ? '◌' : '◉', hidden));
            btns.appendChild(mkBtn('lock', locked ? (acts.unlock || 'Unlock') : (acts.lock || 'Lock'), locked ? '⊠' : '⊡', locked));
            btns.appendChild(mkBtn('up', acts.up || 'Bring forward', '▲', false));
            btns.appendChild(mkBtn('down', acts.down || 'Send backward', '▼', false));
            btns.appendChild(mkBtn('delete', acts.delete || 'Delete', '✕', false));
            el.replaceChildren(iconSpan, nameSpan, btns);
            el.addEventListener('click', (e) => {
                if (e.target.closest('button')) return;
                if (n.getAttr('pmLocked')) return;
                app.konva.transformer.nodes([n]);
            });
            el.querySelector('[data-act=up]').addEventListener('click', (e) => { e.stopPropagation(); n.moveUp(); app.konva.drawLayer.batchDraw(); pushHistory(); refreshLayerPanel(); });
            el.querySelector('[data-act=down]').addEventListener('click', (e) => { e.stopPropagation(); n.moveDown(); app.konva.drawLayer.batchDraw(); pushHistory(); refreshLayerPanel(); });
            el.querySelector('[data-act=delete]').addEventListener('click', (e) => { e.stopPropagation(); n.destroy(); app.konva.drawLayer.batchDraw(); pushHistory(); refreshLayerPanel(); });
            el.querySelector('[data-act=lock]').addEventListener('click', (e) => { e.stopPropagation(); toggleNodeLock(n); refreshLayerPanel(); });
            el.querySelector('[data-act=hide]').addEventListener('click', (e) => { e.stopPropagation(); toggleNodeVisible(n); refreshLayerPanel(); });
            host.appendChild(el);
        });
    }
    // The kind of a mark, as a locale key: what the tools-extra makers stamp in
    // pmKind, else what the built-in tools leave behind.
    function nodeKind(n) {
        const k = n.getAttr('pmKind');
        if (k) return k;
        const cls = n.getClassName();
        if (cls === 'Arrow') return 'arrow';
        if (cls === 'Line') return (n.tension && n.tension() > 0) ? 'highlighter' : 'line';
        if (cls === 'Rect') return (n.fill() && n.fill() !== 'transparent' && !n.strokeWidth()) ? 'solid' : 'rect';
        if (cls === 'Ellipse') return 'ellipse';
        if (cls === 'Path') return 'pen';
        if (cls === 'Text') return n.getAttr('pmKind') === 'sticker' ? 'sticker' : 'text';
        if (cls === 'Group') {
            if (n.getAttr('pmNumber') != null) return 'step';
            if (n.hasName('pm-spotlight')) return 'spotlight';
            return 'group';
        }
        if (cls === 'Image') return n.getAttr('pmFilterKind') || 'blur';
        return 'group';
    }
    function friendlyNodeName(n) {
        const kind = nodeKind(n);
        const names = I18N.layerNames || {};
        if (kind === 'text') return (n.text() || names.text || 'Text').slice(0, 24);
        if (kind === 'step') return (names.step || 'Step') + ' ' + n.getAttr('pmNumber');
        if (kind === 'callout') {
            const t = n.findOne && n.findOne('Text');
            return (t && t.text() ? t.text().slice(0, 24) : (names.callout || 'Callout'));
        }
        return names[kind] || n.getClassName();
    }
    // Icons come from the tool strip itself, so the drawer and the strip agree.
    const STRIP_ICON = {};
    function stripIcon(kind) {
        if (STRIP_ICON[kind] !== undefined) return STRIP_ICON[kind];
        const map = { step: 'number', pen: 'pen', highlighter: 'highlighter', line: 'arrow', solid: 'blur', pixelate: 'blur', angle: 'measure' };
        const btn = document.querySelector('.pm-tool[data-tool="' + (map[kind] || kind) + '"] svg');
        STRIP_ICON[kind] = btn ? btn.outerHTML : '';
        return STRIP_ICON[kind];
    }
    function friendlyNodeIcon(n) {
        const kind = nodeKind(n);
        if (kind === 'step') return String(n.getAttr('pmNumber'));
        if (kind === 'sticker') return (n.text && n.text()) || '☺';
        return stripIcon(kind) || '•';
    }

    // ────────────── Export ──────────────
    // Every file this tool hands over goes through the site's download door: it
    // owns the one-file-per-press budget, the Android WebView relay and the iOS
    // share sheet, and it ANSWERS whether the file was delivered (false — held
    // or refused). The day's unit is billed on that answer, never before it.
    async function deliver(blob, name) {
        if (typeof window.__downloadFile !== 'function') return false;
        const ok = await window.__downloadFile(blob, name);
        return ok !== false;
    }

    async function exportPNG(returnBlob) {
        try {
            const stage = app.konva.stage;
            const tr = app.konva.transformer;
            tr.nodes([]);
            const prevScale = app.state.zoom;
            setZoom(1);
            const pr = app.state.retina || 1;
            const dataUrl = stage.toDataURL({ mimeType: 'image/png', pixelRatio: pr });
            setZoom(prevScale);
            const blob = await (await fetch(dataUrl)).blob();
            if (returnBlob) return blob;
            const ok = await deliver(blob, filename('png', pr));
            if (ok) trackComplete({ format: 'png', mb: Math.round(blob.size / 1048576 * 10) / 10, annot: annotCount(), retina: pr });
            return ok;
        } catch (e) { trackError(e, 'export_png'); throw e; }
    }
    async function exportJPG() {
        try {
            const stage = app.konva.stage;
            app.konva.transformer.nodes([]);
            const prevScale = app.state.zoom; setZoom(1);
            const pr = app.state.retina || 1;
            const dataUrl = stage.toDataURL({ mimeType: 'image/jpeg', quality: 0.92, pixelRatio: pr });
            setZoom(prevScale);
            const blob = await (await fetch(dataUrl)).blob();
            const ok = await deliver(blob, filename('jpg', pr));
            if (ok) trackComplete({ format: 'jpg', mb: Math.round(blob.size / 1048576 * 10) / 10, annot: annotCount(), retina: pr });
            return ok;
        } catch (e) { trackError(e, 'export_jpg'); throw e; }
    }
    // Transparent PNG: annotations only, no background image
    async function exportPNGTransparent() {
        try {
            app.konva.transformer.nodes([]);
            const prevScale = app.state.zoom; setZoom(1);
            // Temporarily hide background, export draw+overlay layers only, restore
            const wasVisible = app.konva.bgLayer.visible();
            app.konva.bgLayer.visible(false);
            app.konva.bgLayer.batchDraw();
            app.konva.drawLayer.batchDraw();
            const pr = app.state.retina || 1;
            const dataUrl = app.konva.stage.toDataURL({ mimeType: 'image/png', pixelRatio: pr });
            app.konva.bgLayer.visible(wasVisible);
            app.konva.bgLayer.batchDraw();
            setZoom(prevScale);
            const blob = await (await fetch(dataUrl)).blob();
            const ok = await deliver(blob, filename('annotations', pr) + '.png');
            if (ok) trackComplete({ format: 'png-transparent', mb: Math.round(blob.size / 1048576 * 10) / 10, annot: annotCount(), retina: pr });
            return ok;
        } catch (e) { trackError(e, 'export_png_transparent'); throw e; }
    }
    function annotCount() {
        try { return app.konva.drawLayer.find('.pm-obj').length; } catch(e) { return 0; }
    }
    async function exportSVG_wrapped() {
        try {
            const ok = await exportSVG();
            if (ok) trackComplete({ format: 'svg', annot: annotCount() });
            return ok;
        } catch(e){ trackError(e, 'export_svg'); throw e; }
    }
    async function exportSVG() {
        // Compose SVG with embedded bitmap + overlay SVG built from Konva nodes
        const W = app.state.imgW, H = app.state.imgH;
        const bgCanvas = document.createElement('canvas');
        bgCanvas.width = W; bgCanvas.height = H;
        bgCanvas.getContext('2d').drawImage(app.konva.imageNode.image(), 0, 0, W, H);
        const bgData = bgCanvas.toDataURL('image/png');

        // Render annotations layer to PNG and embed (preserves exact look including filters)
        const prevScale = app.state.zoom; setZoom(1);
        app.konva.transformer.nodes([]);
        const annotData = app.konva.drawLayer.toDataURL({ pixelRatio: 1 });
        setZoom(prevScale);

        const svg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}">
<image href="${bgData}" x="0" y="0" width="${W}" height="${H}"/>
<image href="${annotData}" x="0" y="0" width="${W}" height="${H}"/>
</svg>`;
        const blob = new Blob([svg], { type: 'image/svg+xml' });
        return deliver(blob, filename('svg'));
    }

    // true — the picture is in the clipboard.
    async function copyToClipboard() {
        try {
            const blob = await exportPNG(true);
            if (navigator.clipboard && window.ClipboardItem) {
                await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
                toast(I18N.copied || 'Copied');
                trackFeature('clipboard_copy_ok', { mb: Math.round(blob.size / 1048576 * 10) / 10, annot: annotCount() });
                trackComplete({ format: 'clipboard', mb: Math.round(blob.size / 1048576 * 10) / 10 });
                return true;
            } else {
                throw new Error('Clipboard API unsupported');
            }
        } catch (e) {
            console.warn(e);
            toast(I18N.copyFail || 'Clipboard blocked — download instead');
            trackError(e, 'clipboard_copy');
            return false;
        }
    }
    // The copy is a hand-over like a download — the menu row and Ctrl+C alike
    // (the shortcut used to copy without ever asking the daily limit): the
    // same unit, spent only when the picture really is in the clipboard.
    async function copyCanvas() {
        try {
            if (!app.konva.stage) return;
            if ($('pmCompare')?.getAttribute('aria-pressed') === 'true') setCompareMode(false);
            const run = beginRun();
            if (!run) return;
            let copied = false;
            try { copied = await copyToClipboard(); }
            catch (err) { run.abort(); handleErr(err); return; }
            if (copied) run.commit(); else run.abort();
        } catch (err) { handleErr(err); }
    }

    function filename(ext, retina) {
        const stamp = new Date().toISOString().slice(0, 19).replace(/[-T:]/g, '');
        const retinaSuffix = retina && retina > 1 ? `@${retina}x` : '';
        // When ext already looks like "annotations" (multi-part name), return base without extension
        if (!/^[a-z0-9]{2,5}$/i.test(ext)) return `photo-markup-${stamp}${retinaSuffix}-${ext}`;
        return `photo-markup-${stamp}${retinaSuffix}.${ext}`;
    }

    // ────────────── Daily cap ──────────────
    // One unit = one PHOTO, not one press: the key is the image stamped at
    // load, so exporting PNG and then JPG of the same picture, or exporting
    // again after fixing a typo, costs nothing extra.
    function beginRun() {
        const lim = window.__toolLimit;
        if (!lim || !lim.begin) return { commit(){}, abort(){} };
        const key = app.state.limitKey || 'session';
        return lim.begin('photo-markup', [key]);
    }
    function handleErr(err) {
        console.error('[photo-markup]', err);
        if (window.__handleError) window.__handleError(err, 'photo-markup');
        else toast(String(err && err.message || err), 'error');
    }

    // ────────────── UI wiring ──────────────
    function bindUI() {
        // Tool buttons
        $$('.pm-tool').forEach(btn => btn.addEventListener('click', () => {
            selectTool(btn.dataset.tool);
        }));

        // Undo/Redo
        $('pmUndo').addEventListener('click', undo);
        $('pmRedo').addEventListener('click', redo);

        // Zoom — read-out click = 100%
        $('pmZoomIn').addEventListener('click', () => setZoom(app.state.zoom * 1.25));
        $('pmZoomOut').addEventListener('click', () => setZoom(app.state.zoom / 1.25));
        $('pmZoomFit').addEventListener('click', fitToView);
        $('pmZoomReadout').addEventListener('click', () => setZoom(1));

        // Overflow "…" menu
        const moreDrop = $('pmMoreBtn')?.parentElement;
        $('pmMoreBtn')?.addEventListener('click', (e) => {
            e.stopPropagation();
            const open = moreDrop.classList.toggle('is-open');
            $('pmMoreBtn').setAttribute('aria-expanded', open ? 'true' : 'false');
            $('pmMoreMenu').hidden = !open;
        });
        document.addEventListener('click', () => {
            if (!moreDrop) return;
            moreDrop.classList.remove('is-open');
            $('pmMoreBtn')?.setAttribute('aria-expanded', 'false');
            if ($('pmMoreMenu')) $('pmMoreMenu').hidden = true;
        });
        $('pmMoreMenu')?.addEventListener('click', (e) => {
            e.stopPropagation();
            // Close menu after any menuitem click
            if (e.target.closest('[role=menuitem]')) {
                moreDrop.classList.remove('is-open');
                $('pmMoreBtn').setAttribute('aria-expanded', 'false');
                $('pmMoreMenu').hidden = true;
            }
        });

        // Rotate / flip / clear
        $('pmRotateLeft').addEventListener('click', () => rotateBg(-90));
        $('pmRotateRight').addEventListener('click', () => rotateBg(90));
        $('pmFlipH').addEventListener('click', () => flipBg('h'));
        $('pmFlipV').addEventListener('click', () => flipBg('v'));
        $('pmClear').addEventListener('click', () => {
            if (!confirm(I18N.clearConfirm || 'Clear?')) return;
            app.konva.drawLayer.destroyChildren();
            app.konva.transformer.nodes([]);
            app.konva.drawLayer.batchDraw();
            app.state.counter = 1;
            pushHistory();
            refreshLayerPanel();
        });

        // Compare-toggle (eye icon in topbar)
        $('pmCompare')?.addEventListener('click', toggleCompareMode);

        // Shortcuts modal
        $('pmShortcuts').addEventListener('click', () => { $('pmShortcutsModal').hidden = false; });
        $('pmShortcutsClose').addEventListener('click', () => { $('pmShortcutsModal').hidden = true; });
        $('pmShortcutsModal').addEventListener('click', (e) => { if (e.target.id === 'pmShortcutsModal') $('pmShortcutsModal').hidden = true; });

        // Download dropdown
        const drop = $('pmDownloadBtn').parentElement;
        $('pmDownloadBtn').addEventListener('click', (e) => {
            e.stopPropagation();
            const open = drop.classList.toggle('is-open');
            $('pmDownloadMenu').hidden = !open;
        });
        document.addEventListener('click', () => { drop.classList.remove('is-open'); $('pmDownloadMenu').hidden = true; });
        // Every delivery goes through here: the split CTA (PNG at the chosen
        // scale), the format rows in its menu, and Ctrl+S. The exports are
        // awaited: until 04.11.2026 the day's unit was spent the moment the
        // press began (before, and whether or not, a file came out) and a
        // failed export was an unhandled rejection (Sentry T4).
        async function runExport(fmt) {
            try {
                drop.classList.remove('is-open');
                $('pmDownloadMenu').hidden = true;
                // No stage — nothing to hand over and nothing to bill. The editor
                // is inert then; this is the backstop.
                if (!app.konva.stage) return;
                // Exit compare-preview mode before any export so the user always
                // exports their actual annotated image, not the clean original.
                if ($('pmCompare')?.getAttribute('aria-pressed') === 'true') setCompareMode(false);
                const run = beginRun();
                if (!run) return;
                const transparent = !!($('pmTransparent') && $('pmTransparent').checked);
                let delivered = false;
                try {
                    if (fmt === 'png') delivered = transparent ? await exportPNGTransparent() : await exportPNG(false);
                    else if (fmt === 'jpg') delivered = await exportJPG();
                    else if (fmt === 'svg') delivered = await exportSVG_wrapped();
                    else if (fmt === 'png-transparent') delivered = await exportPNGTransparent();
                    else if (fmt === 'gif') { trackFeature('gif_export_start'); delivered = await exportGifReveal(); }
                    else if (fmt === 'ba') { trackFeature('ba_export_start'); delivered = await exportBeforeAfter(); }
                } catch (err) {
                    run.abort();
                    handleErr(err);
                    return;
                }
                // Only a DELIVERED file spends the unit; a held or refused one does not.
                if (delivered === true) run.commit(); else run.abort();
            } catch (err) { handleErr(err); }
        }
        app.api.runExport = runExport;
        $$('[data-export]').forEach(btn => btn.addEventListener('click', (e) => {
            e.stopPropagation();
            runExport(btn.dataset.export);
        }));
        $('pmDownloadNow')?.addEventListener('click', () => runExport('png'));
        $('pmCopy').addEventListener('click', copyCanvas);

        // Color
        const colorInput = $('pmColor');
        colorInput.addEventListener('input', () => { app.state.color = colorInput.value; rememberColor(colorInput.value); });
        $$('.pm-swatch').forEach(sw => sw.addEventListener('click', () => {
            colorInput.value = sw.dataset.color;
            app.state.color = sw.dataset.color;
            $$('.pm-swatch').forEach(x => x.classList.remove('is-active'));
            sw.classList.add('is-active');
            rememberColor(sw.dataset.color);
        }));

        // Stroke-width preset chips
        $$('.pm-stroke-chip').forEach(chip => chip.addEventListener('click', () => {
            const v = +chip.dataset.stroke;
            app.state.stroke = v;
            $('pmStroke').value = v;
            setVal('pmStrokeVal', v);
            $$('.pm-stroke-chip').forEach(c => c.classList.toggle('is-active', c === chip));
        }));

        // Sliders
        $('pmStroke').addEventListener('input', (e) => {
            const n = num(e); if (n == null) return;
            app.state.stroke = n; setVal('pmStrokeVal', n);
            // De-highlight chips when slider is used directly
            $$('.pm-stroke-chip').forEach(c => c.classList.toggle('is-active', +c.dataset.stroke === app.state.stroke));
        });
        $('pmOpacity').addEventListener('input', (e) => { const n = num(e); if (n == null) return; app.state.opacity = n / 100; setVal('pmOpacityVal', n); });
        $('pmFontSize').addEventListener('input', (e) => { const n = num(e); if (n == null) return; app.state.fontSize = n; setVal('pmFontSizeVal', n); });
        $('pmFontFamily').addEventListener('change', (e) => { app.state.fontFamily = e.target.value; });
        $('pmTextBg').addEventListener('change', (e) => { app.state.textBg = e.target.checked; });
        $('pmTextOutline').addEventListener('change', (e) => {
            app.state.textOutline = e.target.checked;
            $('pmOutlineRow').hidden = !e.target.checked;
        });
        $('pmOutlineColor').addEventListener('input', (e) => { app.state.outlineColor = e.target.value; });
        $('pmArrowDashed')?.addEventListener('change', (e) => { app.state.arrowDashed = e.target.checked; });

        // Retina preset chips in download dropdown
        $$('[data-retina]').forEach(b => b.addEventListener('click', () => {
            $$('[data-retina]').forEach(x => { x.classList.remove('is-active'); x.setAttribute('aria-checked', 'false'); });
            b.classList.add('is-active'); b.setAttribute('aria-checked', 'true');
            app.state.retina = +b.dataset.retina;
        }));

        // Camera capture (mobile) — <input capture="environment"> inside pm-camera-btn
        $('pmCameraInput')?.addEventListener('change', (e) => {
            const f = e.target.files && e.target.files[0];
            if (f) openImageBlob(f, f.name || 'camera.jpg');
        });
        $('pmShapeFill').addEventListener('change', (e) => { app.state.shapeFill = e.target.checked; });
        $('pmBlurRadius').addEventListener('input', (e) => { const n = num(e); if (n == null) return; app.state.blurRadius = n; setVal('pmBlurRadiusVal', n); });
        $('pmPixelSize').addEventListener('input', (e) => { const n = num(e); if (n == null) return; app.state.pixelSize = n; setVal('pmPixelSizeVal', n); });
        $('pmCounterReset')?.addEventListener('click', () => { app.state.counter = 1; toast(I18N.counterReset || 'Counter reset to 1'); });

        // Arrow style
        $$('[data-arrow-style]').forEach(b => b.addEventListener('click', () => {
            $$('[data-arrow-style]').forEach(x => { x.classList.remove('is-active'); x.setAttribute('aria-checked', 'false'); });
            b.classList.add('is-active'); b.setAttribute('aria-checked', 'true');
            app.state.arrowStyle = b.dataset.arrowStyle;
        }));

        // Right-panel tabs
        $$('.pm-panel-tab').forEach(tab => tab.addEventListener('click', () => {
            activatePanelTab(tab.dataset.panel);
        }));

        // Adjustment sliders (brightness / contrast / saturation / blurAll).
        // Telemetry: dedupe per-kind once-per-session to avoid storms during slider drag —
        // the interesting signal is "this user used this filter at all", not how often.
        const _adjustUsedSent = new Set();
        $$('[data-adjust]').forEach(inp => inp.addEventListener('input', (e) => {
            const kind = inp.dataset.adjust;
            app.state.adjust[kind] = +e.target.value;
            const valEl = document.getElementById(inp.id + 'Val');
            if (valEl) valEl.textContent = kind === 'blurAll' ? e.target.value : (+e.target.value).toFixed(2);
            applyBgFilters();
            if (!_adjustUsedSent.has(kind) && +e.target.value !== 0) {
                _adjustUsedSent.add(kind);
                trackFeature('adjust_use', { kind });
            }
        }));
        // Adjustment reset
        $('pmAdjustReset')?.addEventListener('click', () => {
            app.state.adjust = { brightness: 0, contrast: 0, saturation: 0, blurAll: 0 };
            $$('[data-adjust]').forEach(inp => {
                inp.value = 0;
                const valEl = document.getElementById(inp.id + 'Val');
                if (valEl) valEl.textContent = inp.dataset.adjust === 'blurAll' ? '0' : '0.00';
            });
            applyBgFilters();
        });

        // Crop ratio
        $$('[data-ratio]').forEach(b => b.addEventListener('click', () => {
            $$('[data-ratio]').forEach(x => x.classList.remove('is-active'));
            b.classList.add('is-active');
            app.state.cropRatio = +b.dataset.ratio;
        }));

        // Spotlight options (shape + dim + clear)
        $$('[data-spotlight-shape]').forEach(b => b.addEventListener('click', () => {
            $$('[data-spotlight-shape]').forEach(x => { x.classList.remove('is-active'); x.setAttribute('aria-checked', 'false'); });
            b.classList.add('is-active'); b.setAttribute('aria-checked', 'true');
            app.state.spotlightShape = b.dataset.spotlightShape;
            // If a spotlight already exists, re-render it with the new shape
            const existing = app.konva.drawLayer.find('.pm-spotlight')[0];
            if (existing) {
                const cfg = existing.getAttr('pmSpotlight');
                if (cfg) { createSpotlight(cfg.bbox); pushHistory(); refreshLayerPanel(); }
            }
        }));
        $('pmSpotlightDim')?.addEventListener('input', (e) => {
            app.state.spotlightDim = +e.target.value / 100;
            setVal('pmSpotlightDimVal', e.target.value);
            // Live-update existing spotlight without adding history entry per input event
            const dim = app.konva.drawLayer.find('.pm-spotlight-dim')[0];
            if (dim) { dim.fill(`rgba(0,0,0,${app.state.spotlightDim})`); dim.getParent().cache(); app.konva.drawLayer.batchDraw(); }
        });
        $('pmSpotlightDim')?.addEventListener('change', () => {
            if (app.konva.drawLayer.find('.pm-spotlight').length) pushHistory();
        });
        $('pmSpotlightClear')?.addEventListener('click', () => {
            if (!app.konva.drawLayer.find('.pm-spotlight').length) return;
            clearSpotlight();
            pushHistory();
            refreshLayerPanel();
        });

        // Face detect (lazy)
        $('pmDetectFaces')?.addEventListener('click', runFaceDetection);
        $$('[data-face-action]').forEach(b => b.addEventListener('click', () => faceAction(b.dataset.faceAction)));

        // Mobile options FAB opens right panel as bottom-sheet
        const openSheet = () => {
            $('pmRightPanel').classList.add('is-open');
            $('pmSheetBackdrop').hidden = false;
            $('pmMobileOptionsFab').classList.add('is-hidden');
            $('pmMobileOptionsFab').setAttribute('aria-expanded', 'true');
        };
        const closeSheet = () => {
            $('pmRightPanel').classList.remove('is-open');
            $('pmSheetBackdrop').hidden = true;
            $('pmMobileOptionsFab').classList.remove('is-hidden');
            $('pmMobileOptionsFab').setAttribute('aria-expanded', 'false');
        };
        $('pmMobileOptionsFab')?.addEventListener('click', openSheet);
        $('pmSheetBackdrop')?.addEventListener('click', closeSheet);
        // Close sheet if window resizes past the mobile breakpoint
        window.addEventListener('resize', () => { if (window.innerWidth > 900) closeSheet(); });
        // Close sheet on Escape when open
        window.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && $('pmRightPanel').classList.contains('is-open')) {
                closeSheet();
            }
        });

        // Recovery + downscale modals handled inline elsewhere.

        // Keyboard shortcuts
        window.addEventListener('keydown', onKey);

        // Initialise aria-pressed on every tool button based on current state
        $$('.pm-tool').forEach(b => {
            b.setAttribute('aria-pressed', b.dataset.tool === app.state.tool ? 'true' : 'false');
        });
    }

    function onKey(e) {
        // Skip if user is typing in an input
        const tag = (e.target.tagName || '').toLowerCase();
        if (tag === 'input' || tag === 'textarea' || e.target.isContentEditable) return;
        // Every key below acts on the stage. Before a picture, or while the stage
        // still waits for Konva, they threw on a stage that did not exist (the
        // arrows were Sentry KX; + − 0 1 and Ctrl+S the T3/T4 class) — and Ctrl+S
        // on the start screen spent the day's unit on nothing. Space scrolls the
        // page again while there is no editor to pan.
        if (!editorLive()) return;

        const ctrl = e.ctrlKey || e.metaKey;
        const k = e.key;
        const kl = window.__keyLetter ? window.__keyLetter(e) : String(k || '').toLowerCase();

        // Space pan
        if (k === ' ') { app.ui.spaceHeld = true; e.preventDefault(); return; }

        if (ctrl && kl === 'z' && !e.shiftKey) { undo(); e.preventDefault(); return; }
        if (ctrl && ((kl === 'z' && e.shiftKey) || kl === 'y')) { redo(); e.preventDefault(); return; }
        if (ctrl && kl === 's') { if (app.api.runExport) app.api.runExport('png'); e.preventDefault(); return; }
        // Clipboard: objects if selection exists, else copy canvas
        if (ctrl && kl === 'c') {
            if (getSelection().length) { copySelection(); e.preventDefault(); return; }
            copyCanvas(); e.preventDefault(); return;
        }
        // Paste: first try to paste objects from our clipboard; clipboard image paste is handled by window `paste` event
        if (ctrl && kl === 'v') {
            if (PMClipboard.items && PMClipboard.items.length) {
                pasteObjects({ dx: 16, dy: 16 });
                e.preventDefault();
                return;
            }
        }
        if (ctrl && kl === 'd') { duplicateSelection(); e.preventDefault(); return; }
        if (ctrl && kl === 'a') { selectAll(); e.preventDefault(); return; }
        if (k === 'Delete' || k === 'Backspace') {
            const sel = getSelection();   // [] before any image (Sentry KX)
            if (sel.length) { sel.forEach(n => n.destroy()); app.konva.transformer.nodes([]); app.konva.drawLayer.batchDraw(); pushHistory(); refreshLayerPanel(); e.preventDefault(); }
            return;
        }
        if (k === 'Escape') { if (app.konva.transformer) app.konva.transformer.nodes([]); $('pmShortcutsModal').hidden = true; return; }
        if (k === '?') { $('pmShortcutsModal').hidden = false; return; }
        if (k === '+' || k === '=') { setZoom(app.state.zoom * 1.25); e.preventDefault(); return; }
        if (k === '-') { setZoom(app.state.zoom / 1.25); e.preventDefault(); return; }
        if (k === '0') { fitToView(); e.preventDefault(); return; }
        if (k === '1') { setZoom(1); e.preventDefault(); return; }

        // Arrow keys nudge selected (shift = larger step)
        const nudgeStep = e.shiftKey ? 10 : 1;
        if (k === 'ArrowLeft' && getSelection().length)  { nudgeSelection(-nudgeStep, 0); pushHistory(); e.preventDefault(); return; }
        if (k === 'ArrowRight' && getSelection().length) { nudgeSelection(nudgeStep, 0);  pushHistory(); e.preventDefault(); return; }
        if (k === 'ArrowUp' && getSelection().length)    { nudgeSelection(0, -nudgeStep); pushHistory(); e.preventDefault(); return; }
        if (k === 'ArrowDown' && getSelection().length)  { nudgeSelection(0, nudgeStep);  pushHistory(); e.preventDefault(); return; }

        // Tool shortcuts. Every letter the shortcuts modal prints has to land
        // somewhere: E/K/M/D are the marks added in the rebuild, and L/X/F are
        // the keys of tools that were MERGED — they still work, they now pick
        // the merged tool plus the variant they used to be.
        const map = {
            v:'select', a:'arrow', l:'arrow', r:'rect', o:'ellipse',
            p:'pen', h:'highlighter', t:'text', e:'callout', n:'number',
            k:'sticker', b:'blur', x:'blur', f:'blur', s:'spotlight',
            m:'magnifier', d:'measure', c:'crop', i:'eyedropper', y:'eraser',
        };
        const letter = kl;
        if (!ctrl && !e.altKey && map[letter]) {
            selectTool(map[letter]);
            // The variant (line vs arrow, pixels vs blur, run the face sweep)
            // belongs to the tools that own those switches.
            if (window.PMX && PMX.onShortcut) PMX.onShortcut(letter);
        }
    }
    window.addEventListener('keyup', (e) => { if (e.key === ' ') app.ui.spaceHeld = false; });

    function activatePanelTab(which) {
        $$('.pm-panel-tab').forEach(t => {
            const on = t.dataset.panel === which;
            t.classList.toggle('is-active', on);
            t.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        $$('[data-panel-content]').forEach(p => {
            p.hidden = (p.dataset.panelContent !== which);
        });
    }

    function selectTool(toolName) {
        if (toolName !== app.state.tool) trackToolUsed(toolName);
        app.state.tool = toolName;
        $$('.pm-tool').forEach(b => {
            const active = b.dataset.tool === toolName;
            b.classList.toggle('is-active', active);
            b.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        $('pmCanvasArea').dataset.tool = toolName;
        // While a DRAWING tool is active, existing marks must not swallow the
        // gesture: pointerdown landed on the last arrow instead of the canvas,
        // so a second mark could not be started anywhere a first one already
        // covered — on a busy screenshot that reads as "it stopped drawing".
        // Only Select (move/resize) and Eraser (click to remove) need the marks
        // to be hit-testable.
        if (app.konva.drawLayer) {
            const interactive = (toolName === 'select' || toolName === 'eraser');
            app.konva.drawLayer.listening(interactive);
            if (!interactive && app.konva.transformer) {
                app.konva.transformer.nodes([]);
                app.konva.overlayLayer && app.konva.overlayLayer.batchDraw();
            }
        }
        // Auto-switch right panel to Style tab so tool options are immediately visible
        activatePanelTab('style');
        const show = (sel, cond) => { const el = document.querySelector(sel); if (el) el.hidden = !cond; };
        show('.pm-ctx-text', toolName === 'text');
        show('.pm-ctx-shape', toolName === 'rect' || toolName === 'ellipse');
        // Arrow + line share the "arrow style + dashed" options
        document.querySelectorAll('.pm-ctx-arrow, .pm-ctx-line').forEach(el => {
            el.hidden = !(toolName === 'arrow' || toolName === 'line');
        });
        show('.pm-ctx-blur', toolName === 'blur');
        show('.pm-ctx-pixelate', toolName === 'pixelate');
        show('.pm-ctx-number', toolName === 'number');
        show('.pm-ctx-crop', toolName === 'crop');
        show('.pm-ctx-faceblur', toolName === 'faceblur');
        show('.pm-ctx-spotlight', toolName === 'spotlight');
        // Tools added in tools-extra.js own their own context blocks and their
        // own cue line; the event carries the switch so nothing has to be
        // listed twice.
        document.dispatchEvent(new CustomEvent('pm:tool', { detail: { tool: toolName } }));
        // Drop selection when leaving select tool
        if (toolName !== 'select' && app.konva.transformer) app.konva.transformer.nodes([]);

        // Special-tool activations
        clearCrop();
        if (toolName === 'crop') activateCrop();
    }

    function rememberColor(c) {
        const list = app.state.recent;
        const ix = list.indexOf(c);
        if (ix !== -1) list.splice(ix, 1);
        list.unshift(c);
        if (list.length > 8) list.pop();
        const host = $('pmRecent');
        if (!host) return;
        host.innerHTML = '';
        list.forEach(col => {
            const b = document.createElement('button');
            b.type = 'button';
            b.className = 'pm-swatch';
            b.style.background = col;
            b.dataset.color = col;
            b.addEventListener('click', () => { app.state.color = col; $('pmColor').value = col; });
            host.appendChild(b);
        });
        $('pmRecentWrap').hidden = list.length === 0;
    }

    // ────────────── Bg image rotate / flip ──────────────
    function rotateBg(deg) {
        const src = app.konva.imageNode.image();
        const prevW = app.state.imgW, prevH = app.state.imgH;
        const cv = document.createElement('canvas');
        if (deg % 180 !== 0) { cv.width = prevH; cv.height = prevW; }
        else { cv.width = prevW; cv.height = prevH; }
        const ctx = cv.getContext('2d');
        ctx.save();
        ctx.translate(cv.width / 2, cv.height / 2);
        ctx.rotate(deg * Math.PI / 180);
        ctx.drawImage(src, -prevW / 2, -prevH / 2);
        ctx.restore();
        swapBg(cv);
    }
    function flipBg(dir) {
        const src = app.konva.imageNode.image();
        const cv = document.createElement('canvas');
        cv.width = app.state.imgW; cv.height = app.state.imgH;
        const ctx = cv.getContext('2d');
        if (dir === 'h') { ctx.translate(cv.width, 0); ctx.scale(-1, 1); }
        else { ctx.translate(0, cv.height); ctx.scale(1, -1); }
        ctx.drawImage(src, 0, 0);
        swapBg(cv);
    }
    function swapBg(canvas) {
        app.state.imgW = canvas.width; app.state.imgH = canvas.height;
        app.konva.imageNode.image(canvas);
        app.konva.imageNode.width(canvas.width);
        app.konva.imageNode.height(canvas.height);
        app.konva.bgLayer.batchDraw();
        fitToView();
        pushHistory();
    }

    function applyBgFilters() {
        const img = app.konva.imageNode;
        const filters = [];
        const { brightness, contrast, saturation, blurAll } = app.state.adjust;
        img.cache();
        if (brightness !== 0) { filters.push(Konva.Filters.Brighten); img.brightness(brightness); }
        if (contrast !== 0) { filters.push(Konva.Filters.Contrast); img.contrast(contrast * 100); }
        if (saturation !== 0) { filters.push(Konva.Filters.HSL); img.saturation(saturation); }
        if (blurAll > 0) { filters.push(Konva.Filters.Blur); img.blurRadius(blurAll); }
        img.filters(filters);
        app.konva.bgLayer.batchDraw();
    }

    // ────────────── Face detection (lazy) ──────────────
    let faceapi_loading = null;
    let faceState = { boxes: [] };
    // Load a script, trying local VPS static first, then VPS cdn_cache proxy (RU-friendly), then raw jsDelivr.
    function loadScriptWithFallback(urls) {
        return new Promise((resolve, reject) => {
            let i = 0;
            const tryNext = () => {
                if (i >= urls.length) return reject(new Error('all sources failed'));
                const url = urls[i++];
                const s = document.createElement('script');
                s.src = url;
                s.onload = () => resolve(url);
                s.onerror = () => { console.warn('[photo-markup] script failed:', url); tryNext(); };
                document.head.appendChild(s);
            };
            tryNext();
        });
    }
    async function ensureFaceApi() {
        // Ready means the MODEL is loaded, not the script: a first attempt whose
        // weights failed left window.faceapi defined, and the second click went
        // straight into detectAllFaces → «load model before inference» (Sentry M4).
        const net = window.faceapi && window.faceapi.nets && window.faceapi.nets.tinyFaceDetector;
        if (net && net.isLoaded) return window.faceapi;
        if (faceapi_loading) return faceapi_loading;
        faceapi_loading = (async () => {
            await loadScriptWithFallback([
                '/vendor/face-api/face-api.min.js',
                '/cdn/jsdelivr/npm/face-api.js@0.22.2/dist/face-api.min.js',
            ]);
            // Disable TF.js' WebGL GPU fence (gl.fenceSync → gl.clientWaitSync):
            // on some mobile GPUs fenceSync() returns null under memory pressure,
            // so clientWaitSync(null) throws inside TF.js' OWN detached polling
            // promise — an uncatchable onunhandledrejection (TIMBRICA-FRONTEND-AF).
            try {
                const tf = window.faceapi && window.faceapi.tf;
                if (tf && typeof tf.env === 'function' && typeof tf.env().set === 'function') {
                    tf.env().set('WEBGL_FENCE_API_ENABLED', false);
                }
            } catch (e) { /* best effort */ }
            // Model files: try local → VPS cdn_cache of GitHub raw
            const modelSources = [
                '/vendor/face-api/models',
                '/cdn/jsdelivr/gh/justadudewhohacks/face-api.js@master/weights',
            ];
            let lastErr;
            for (const base of modelSources) {
                try {
                    await window.faceapi.nets.tinyFaceDetector.loadFromUri(base);
                    return window.faceapi;
                } catch (e) { lastErr = e; console.warn('[photo-markup] model load failed from', base); }
            }
            throw lastErr;
        })();
        faceapi_loading.catch(() => { faceapi_loading = null; });   // a failed load may be retried

        return faceapi_loading;
    }
    async function runFaceDetection() {
        const status = $('pmFacesStatus');
        status.textContent = I18N.facesLoading || 'Loading…';
        trackFeature('face_detect_start');
        const t0 = performance.now();
        try {
            const fa = await ensureFaceApi();
            const src = app.konva.imageNode.image();
            const opts = new fa.TinyFaceDetectorOptions({ inputSize: 416, scoreThreshold: 0.35 });
            const detections = await fa.detectAllFaces(src, opts);
            faceState.boxes = detections.map(d => d.box);
            status.textContent = fmt(I18N.facesFound || 'Found {n}', { n: detections.length });
            $('pmFacesActions').hidden = detections.length === 0;
            if (!detections.length) status.textContent = I18N.facesNone || 'No faces';
            drawFaceOverlay();
            trackFeature('face_detect_ok', { count: detections.length, ms: Math.round(performance.now() - t0) });
        } catch (e) {
            console.error(e);
            status.textContent = I18N.errFaces || 'Detection failed';
            trackError(e, 'face_detect');
        }
    }
    function drawFaceOverlay() {
        // Draw temp rectangles on overlay layer so user sees detections
        app.konva.overlayLayer.find('.pm-face-box').forEach(n => n.destroy());
        faceState.boxes.forEach((b, i) => {
            const r = new Konva.Rect({
                name: 'pm-face-box', x: b.x, y: b.y, width: b.width, height: b.height,
                stroke: '#34d399', strokeWidth: 3, dash: [6, 4], fillEnabled: false,
                listening: false,
            });
            app.konva.overlayLayer.add(r);
        });
        app.konva.overlayLayer.batchDraw();
    }
    function faceAction(kind) {
        if (kind === 'clear') {
            faceState.boxes = [];
            app.konva.overlayLayer.find('.pm-face-box').forEach(n => n.destroy());
            app.konva.overlayLayer.batchDraw();
            $('pmFacesStatus').textContent = '';
            $('pmFacesActions').hidden = true;
            return;
        }
        faceState.boxes.forEach(b => {
            if (kind === 'blur') applyAreaFilter('blur', { x: b.x, y: b.y, w: b.width, h: b.height });
            else if (kind === 'pixel') applyAreaFilter('pixelate', { x: b.x, y: b.y, w: b.width, h: b.height });
        });
        faceAction('clear');
    }

    // ────────────── Shape recognition (WOW) ──────────────
    // Ramer-Douglas-Peucker simplification + heuristics: rough circle/rect/arrow → perfect
    function rdpSimplify(points, epsilon) {
        if (points.length < 3) return points;
        const dmax = { i: 0, d: 0 };
        const [a, b] = [points[0], points[points.length - 1]];
        for (let i = 1; i < points.length - 1; i++) {
            const d = pointLineDistance(points[i], a, b);
            if (d > dmax.d) { dmax.i = i; dmax.d = d; }
        }
        if (dmax.d > epsilon) {
            const l = rdpSimplify(points.slice(0, dmax.i + 1), epsilon);
            const r = rdpSimplify(points.slice(dmax.i), epsilon);
            return l.slice(0, -1).concat(r);
        }
        return [a, b];
    }
    function pointLineDistance(p, a, b) {
        const dx = b[0] - a[0], dy = b[1] - a[1];
        if (dx === 0 && dy === 0) return Math.hypot(p[0] - a[0], p[1] - a[1]);
        const t = ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / (dx * dx + dy * dy);
        const x = a[0] + t * dx, y = a[1] + t * dy;
        return Math.hypot(p[0] - x, p[1] - y);
    }
    function recognizeShape(rawPoints) {
        if (!rawPoints || rawPoints.length < 8) return null;
        const pts = rawPoints.map(p => [p[0], p[1]]);
        const first = pts[0], last = pts[pts.length - 1];
        // Bounding box
        let minX=Infinity,minY=Infinity,maxX=-Infinity,maxY=-Infinity;
        pts.forEach(p => { if(p[0]<minX)minX=p[0]; if(p[1]<minY)minY=p[1]; if(p[0]>maxX)maxX=p[0]; if(p[1]>maxY)maxY=p[1]; });
        const w = maxX - minX, h = maxY - minY;
        if (w < 20 || h < 20) return null;
        // Path length
        let pathLen = 0;
        for (let i = 1; i < pts.length; i++) pathLen += Math.hypot(pts[i][0] - pts[i-1][0], pts[i][1] - pts[i-1][1]);
        const closureDist = Math.hypot(last[0] - first[0], last[1] - first[1]);
        const isClosed = closureDist < Math.max(w, h) * 0.18;
        // Circle: pathLen ≈ π*avgRadius*2, error low
        if (isClosed) {
            const cx = (minX + maxX) / 2, cy = (minY + maxY) / 2;
            const rx = w / 2, ry = h / 2;
            let circErr = 0;
            for (const p of pts) {
                const e = Math.hypot((p[0]-cx)/rx, (p[1]-cy)/ry) - 1;
                circErr += e * e;
            }
            circErr = Math.sqrt(circErr / pts.length);
            if (circErr < 0.22 && Math.abs(w - h) / Math.max(w, h) < 0.35) {
                return { kind: 'circle', bbox: { x: minX, y: minY, w, h }, confidence: 1 - circErr };
            }
            // Rectangle: simplify → ~4 corners
            const simp = rdpSimplify(pts, Math.min(w, h) * 0.1);
            if (simp.length >= 4 && simp.length <= 7) {
                return { kind: 'rect', bbox: { x: minX, y: minY, w, h }, confidence: 0.8 };
            }
        } else {
            // Straight line (for arrow/line): endpoints distance ≈ path length
            const straightness = closureDist / pathLen;
            if (straightness > 0.85 && pathLen > 30) {
                return { kind: 'line', a: first, b: last, confidence: straightness };
            }
        }
        return null;
    }
    function maybeRecognize(d) {
        if (!d || d.type !== 'pen' || !d.rawPoints) return false;
        const shape = recognizeShape(d.rawPoints);
        if (!shape) return false;
        const common = { name: 'pm-obj', stroke: app.state.color, strokeWidth: app.state.stroke, opacity: app.state.opacity, draggable: true };
        let node;
        if (shape.kind === 'circle') {
            node = new Konva.Ellipse({
                ...common, x: shape.bbox.x + shape.bbox.w / 2, y: shape.bbox.y + shape.bbox.h / 2,
                radiusX: shape.bbox.w / 2, radiusY: shape.bbox.h / 2,
                fill: app.state.shapeFill ? app.state.color : 'transparent',
            });
        } else if (shape.kind === 'rect') {
            node = new Konva.Rect({
                ...common, x: shape.bbox.x, y: shape.bbox.y, width: shape.bbox.w, height: shape.bbox.h,
                cornerRadius: 4, fill: app.state.shapeFill ? app.state.color : 'transparent',
            });
        } else if (shape.kind === 'line') {
            node = new Konva.Arrow({
                ...common, points: [shape.a[0], shape.a[1], shape.b[0], shape.b[1]],
                fill: app.state.color,
                pointerLength: Math.max(10, app.state.stroke * 2.5),
                pointerWidth: Math.max(10, app.state.stroke * 2.5),
            });
        }
        if (node) {
            d.node.destroy();
            app.konva.drawLayer.add(node);
            app.konva.drawLayer.batchDraw();
            toast(I18N.shapeRecognized || 'Shape recognized');
            trackFeature('shape_recognized', { kind: shape.kind, confidence: Math.round(shape.confidence * 100) });
            return true;
        }
        return false;
    }

    // ────────────── Spotlight tool ──────────────
    // One spotlight at a time. Implemented as a Konva.Group on drawLayer so it
    // persists through undo/redo, moves/resizes via transformer, and exports.
    // Group contents: full-image dim rect + cut-out shape using 'destination-out'.
    function clearSpotlight() {
        const existing = app.konva.drawLayer.find('.pm-spotlight');
        existing.forEach(n => n.destroy());
        app.konva.drawLayer.batchDraw();
    }
    function createSpotlight(bbox) {
        clearSpotlight();
        const W = app.state.imgW, H = app.state.imgH;
        const group = new Konva.Group({
            name: 'pm-obj pm-spotlight', x: 0, y: 0, draggable: false,
        });
        group.setAttr('pmSpotlight', {
            shape: app.state.spotlightShape,
            dim: app.state.spotlightDim,
            bbox: { ...bbox },
        });
        const dim = new Konva.Rect({
            name: 'pm-spotlight-dim',
            x: 0, y: 0, width: W, height: H,
            fill: `rgba(0,0,0,${app.state.spotlightDim})`,
            listening: false,
        });
        let cut;
        if (app.state.spotlightShape === 'ellipse') {
            cut = new Konva.Ellipse({
                name: 'pm-spotlight-cut',
                x: bbox.x + bbox.w / 2, y: bbox.y + bbox.h / 2,
                radiusX: bbox.w / 2, radiusY: bbox.h / 2,
                fill: '#fff', globalCompositeOperation: 'destination-out',
                listening: false,
            });
        } else {
            cut = new Konva.Rect({
                name: 'pm-spotlight-cut',
                x: bbox.x, y: bbox.y, width: bbox.w, height: bbox.h,
                fill: '#fff', cornerRadius: 6,
                globalCompositeOperation: 'destination-out',
                listening: false,
            });
        }
        group.add(dim);
        group.add(cut);
        // Compositing requires caching the group onto its own bitmap
        group.cache();
        app.konva.drawLayer.add(group);
        app.konva.drawLayer.batchDraw();
        trackFeature('spotlight_create', { shape: app.state.spotlightShape });
    }

    // ────────────── Crop tool ──────────────
    let cropBox = null;
    function activateCrop() {
        clearCrop();
        const cx = app.state.imgW / 2, cy = app.state.imgH / 2;
        const w = app.state.imgW * 0.6, h = app.state.imgH * 0.6;
        cropBox = new Konva.Rect({
            name: 'pm-crop-box', x: cx - w/2, y: cy - h/2, width: w, height: h,
            stroke: '#7aa7ff', strokeWidth: 2, dash: [8, 6],
            fill: 'transparent', draggable: true,
        });
        app.konva.overlayLayer.add(cropBox);
        app.konva.transformer.nodes([cropBox]);
        app.konva.overlayLayer.batchDraw();
    }
    function clearCrop() {
        if (cropBox) { cropBox.destroy(); cropBox = null; }
        // selectTool() ends here, and it is reachable before any stage exists
        if (app.konva.transformer) app.konva.transformer.nodes([]);
        if (app.konva.overlayLayer) app.konva.overlayLayer.batchDraw();
    }
    function applyCrop() {
        if (!cropBox) return;
        const b = { x: Math.max(0, cropBox.x()), y: Math.max(0, cropBox.y()),
                    w: Math.min(app.state.imgW - cropBox.x(), cropBox.width() * cropBox.scaleX()),
                    h: Math.min(app.state.imgH - cropBox.y(), cropBox.height() * cropBox.scaleY()) };
        if (b.w < 8 || b.h < 8) { clearCrop(); return; }
        const src = app.konva.imageNode.image();
        const cv = document.createElement('canvas');
        cv.width = b.w; cv.height = b.h;
        cv.getContext('2d').drawImage(src, b.x, b.y, b.w, b.h, 0, 0, b.w, b.h);
        // Shift existing annotations to new origin
        app.konva.drawLayer.find('.pm-obj').forEach(n => {
            n.x((n.x() || 0) - b.x);
            n.y((n.y() || 0) - b.y);
        });
        clearCrop();
        swapBg(cv);
        pushHistory();
        refreshLayerPanel();
        selectTool('select');
        trackFeature('crop_apply', { w: Math.round(b.w), h: Math.round(b.h), ratio: app.state.cropRatio || 0 });
        // New cropped dims become the session reference
        if (app.session) { app.session.w = Math.round(b.w); app.session.h = Math.round(b.h); }
    }

    // ────────────── GIF reveal export (lazy gif.js) ──────────────
    let gifjsLoading = null;
    function ensureGifJs() {
        if (window.GIF) return Promise.resolve(window.GIF);
        if (gifjsLoading) return gifjsLoading;
        gifjsLoading = loadScriptWithFallback([
            '/vendor/gif.js/gif.js',
            '/cdn/jsdelivr/npm/gif.js@0.2.0/dist/gif.js',
        ]).then(() => window.GIF);
        // a failed load may be retried — the next GIF press loads again (as face-api, M4)
        gifjsLoading.catch(() => { gifjsLoading = null; });
        return gifjsLoading;
    }
    // gif.worker URL also has fallback (used inside new GIF({ workerScript }))
    function gifWorkerUrl() {
        return '/vendor/gif.js/gif.worker.js';
    }
    async function exportGifReveal() {
        toast(I18N.gifExporting || 'Building GIF…', 30000);
        const t0 = performance.now();
        const prevScale = app.state.zoom;
        try {
            const GIF = await ensureGifJs();
            app.konva.transformer.nodes([]);
            setZoom(1);

            const nodes = app.konva.drawLayer.find('.pm-obj');
            // Hide all (and stash original opacity so post-export restore is exact)
            nodes.forEach(n => {
                n.setAttr('pmRevealOrigOpacity', n.opacity());
                n.visible(false);
            });
            app.konva.drawLayer.batchDraw();

            const W = app.state.imgW, H = app.state.imgH;
            const maxSide = 900;
            const scale = Math.min(1, maxSide / Math.max(W, H));
            const gw = Math.round(W * scale), gh = Math.round(H * scale);

            const gif = new GIF({
                workers: 2, workerScript: '/vendor/gif.js/gif.worker.js',
                quality: 10, width: gw, height: gh,
            });

            const addFrame = async (delay) => gif.addFrame(await renderFrameCanvas(gw, gh), { delay });

            // Initial frame (no annotations)
            await addFrame(800);

            // Per-object reveal with class-aware sub-animation
            for (const n of nodes) {
                await animateNodeReveal(n, addFrame);
            }
            // Hold final
            await addFrame(1500);

            const blob = await renderGif(gif);
            setZoom(prevScale);
            // Cleanup stashed attrs
            nodes.forEach(n => n.setAttr('pmRevealOrigOpacity', undefined));
            untoast();
            trackFeature('gif_export_ok', {
                annot: nodes.length,
                mb: Math.round(blob.size / 1048576 * 100) / 100,
                ms: Math.round(performance.now() - t0),
            });
            // «Saved» is the download door's to say; a failure reaches the person
            // through runExport → handleErr, in their language.
            return await deliver(blob, filename('gif'));
        } catch (e) {
            console.error('GIF export', e);
            untoast();
            setZoom(prevScale);
            trackError(e, 'gif_export');
            throw e;
        }
    }
    // gif.js answers with 'finished' — or with nothing at all: a worker that never
    // starts raises no error the page can hear. The run must end either way, so
    // a long silence (visible time) ends the wait.
    const GIF_SILENCE_MS = 30000;
    function renderGif(gif) {
        return new Promise((resolve, reject) => {
            let done = false, cancel = null;
            const stop = () => { done = true; if (cancel) cancel(); };
            const arm = () => {
                if (done) return;
                if (cancel) cancel();
                const stalled = () => {
                    if (done) return;
                    stop();
                    try { gif.abort(); } catch (_) {}
                    reject(Object.assign(new Error('GIF render stalled'), { code: 'GIF_STALLED' }));
                };
                if (window.__visibleTimeout) cancel = window.__visibleTimeout(stalled, GIF_SILENCE_MS);
                else { const h = setTimeout(stalled, GIF_SILENCE_MS); cancel = () => clearTimeout(h); }
            };
            gif.on('progress', arm);
            gif.on('finished', (blob) => { if (done) return; stop(); resolve(blob); });
            gif.on('abort', () => { if (done) return; stop(); reject(Object.assign(new Error('GIF render aborted'), { code: 'GIF_ABORTED' })); });
            arm();
            try { gif.render(); } catch (e) { stop(); reject(e); }
        });
    }

    // Animate a single annotation's appearance into a sequence of GIF frames.
    // Class-aware: typewriter for Text, dashOffset draw for Arrow/Line/Path,
    // opacity fade for everything else (Image/Rect/Ellipse/Group). Capped at
    // a few sub-frames per node so overall GIF size and render time stay
    // bounded for users with 20+ annotations.
    async function animateNodeReveal(n, addFrame) {
        const klass = n.getClassName ? n.getClassName() : '';
        const origOpacity = n.getAttr('pmRevealOrigOpacity');
        n.visible(true);

        if (klass === 'Text' && typeof n.text === 'function' && n.text() && n.text().length > 1) {
            // Typewriter: reveal in 5-char chunks, max 6 sub-frames so a long
            // caption doesn't blow up frame count. Start invisible-text node,
            // grow it up to full content, then a short hold.
            const full = n.text();
            const chunks = Math.min(6, Math.ceil(full.length / 5));
            for (let i = 1; i <= chunks; i++) {
                const cut = Math.round(full.length * (i / chunks));
                n.text(full.slice(0, cut));
                app.konva.drawLayer.draw();
                await addFrame(80);
            }
            n.text(full);
            app.konva.drawLayer.draw();
            await addFrame(280);
            return;
        }

        // Stroke-able classes with getLength(): Arrow / Line / Path. Animate a
        // dash-offset along the stroke length so the line "draws itself".
        if ((klass === 'Arrow' || klass === 'Line' || klass === 'Path') && typeof n.getLength === 'function') {
            try {
                const len = Math.max(1, n.getLength());
                const origDash = n.dash() ? n.dash().slice() : null;
                const origOffset = n.dashOffset() || 0;
                n.dash([len, len]);
                const steps = 5;
                for (let s = 1; s <= steps; s++) {
                    n.dashOffset(len * (1 - s / steps));
                    app.konva.drawLayer.draw();
                    await addFrame(70);
                }
                // Restore (so final frame matches user's actual setup)
                if (origDash && origDash.length) n.dash(origDash);
                else n.dash([]);
                n.dashOffset(origOffset);
                app.konva.drawLayer.draw();
                await addFrame(280);
                return;
            } catch (_) { /* fall through to fade */ }
        }

        // Default: opacity fade-in (3 sub-frames). Works for Rect / Ellipse /
        // Image (blur/pixelate placeholders) / Group (numbered badges, spotlights).
        const target = (typeof origOpacity === 'number') ? origOpacity : 1;
        const steps = 3;
        for (let s = 1; s <= steps; s++) {
            n.opacity(target * (s / steps));
            app.konva.drawLayer.draw();
            await addFrame(90);
        }
        n.opacity(target);
        app.konva.drawLayer.draw();
        await addFrame(280);
    }
    async function renderFrameCanvas(w, h) {
        const stage = app.konva.stage;
        const dataUrl = stage.toDataURL({ pixelRatio: w / app.state.imgW });
        const img = new Image();
        img.src = dataUrl;
        await new Promise(r => img.onload = r);
        const cv = document.createElement('canvas');
        cv.width = w; cv.height = h;
        cv.getContext('2d').drawImage(img, 0, 0, w, h);
        return cv;
    }

    // ────────────── Before/After slider export ──────────────
    async function exportBeforeAfter() {
        toast(I18N.baExporting || 'Building slider…', 30000);
        try {
            app.konva.transformer.nodes([]);
            const prevScale = app.state.zoom; setZoom(1);
            // Before (clean image)
            const cv = document.createElement('canvas');
            cv.width = app.state.imgW; cv.height = app.state.imgH;
            cv.getContext('2d').drawImage(app.konva.imageNode.image(), 0, 0);
            const beforeData = cv.toDataURL('image/jpeg', 0.88);
            // After (with annotations flattened)
            const afterData = app.konva.stage.toDataURL({ mimeType: 'image/jpeg', quality: 0.88 });
            setZoom(prevScale);

            const html = `<!doctype html><html><head><meta charset="utf-8"><title>Before / After</title><style>
*{box-sizing:border-box;margin:0;padding:0}body{background:#111;color:#fff;font-family:system-ui,-apple-system,sans-serif;min-height:100vh;display:grid;place-items:center;padding:20px}
.ba-wrap{position:relative;max-width:1200px;width:100%;aspect-ratio:${app.state.imgW}/${app.state.imgH};overflow:hidden;border-radius:12px;box-shadow:0 20px 60px rgba(0,0,0,.6);user-select:none}
.ba-img{position:absolute;inset:0;width:100%;height:100%;object-fit:contain;background:#000}
.ba-img.after{clip-path:inset(0 0 0 50%);transition:clip-path 0s}
.ba-handle{position:absolute;top:0;bottom:0;left:50%;width:3px;background:#fff;box-shadow:0 0 10px rgba(0,0,0,.6);cursor:ew-resize;pointer-events:none}
.ba-knob{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:40px;height:40px;border-radius:50%;background:#fff;box-shadow:0 4px 14px rgba(0,0,0,.5);display:grid;place-items:center;pointer-events:auto;cursor:ew-resize}
.ba-knob::before{content:'⇆';color:#111;font-weight:bold}
.ba-label{position:absolute;top:10px;padding:4px 10px;background:rgba(0,0,0,.6);border-radius:6px;font-size:13px}
.ba-label.before{left:10px}.ba-label.after{right:10px}
h1{font-size:16px;margin-bottom:12px;opacity:.7}
</style></head><body>
<div><h1>Before / After — drag the handle</h1>
<div class="ba-wrap" id="wrap">
<img class="ba-img before" src="${beforeData}">
<img class="ba-img after" id="after" src="${afterData}">
<div class="ba-label before">Before</div>
<div class="ba-label after">After</div>
<div class="ba-handle" id="handle"><div class="ba-knob" id="knob"></div></div>
</div></div>
<script>
const wrap=document.getElementById('wrap'),h=document.getElementById('handle'),a=document.getElementById('after');
let dragging=false;
function move(e){const r=wrap.getBoundingClientRect();const x=Math.max(0,Math.min(r.width,(e.clientX||e.touches?.[0]?.clientX||0)-r.left));const pct=x/r.width*100;h.style.left=pct+'%';a.style.clipPath='inset(0 0 0 '+pct+'%)';}
document.getElementById('knob').addEventListener('pointerdown',e=>{dragging=true;e.preventDefault();});
window.addEventListener('pointermove',e=>{if(dragging)move(e);});
window.addEventListener('pointerup',()=>dragging=false);
wrap.addEventListener('click',move);
<\/script>
</body></html>`;
            const blob = new Blob([html], { type: 'text/html' });
            untoast();
            return await deliver(blob, filename('html'));
        } catch (e) {
            console.error(e);
            untoast();
            trackError(e, 'export_ba');
            throw e;
        }
    }

    // ────────────── IndexedDB autosave ──────────────
    const IDB_KEY = 'photo-markup-state';
    let idbDb = null;
    function idbOpen() {
        if (idbDb) return Promise.resolve(idbDb);
        return new Promise((resolve, reject) => {
            const req = indexedDB.open('photo-markup', 1);
            req.onupgradeneeded = () => req.result.createObjectStore('state');
            req.onsuccess = () => { idbDb = req.result; resolve(idbDb); };
            req.onerror = () => reject(req.error);
        });
    }
    async function idbSave(data) {
        try {
            const db = await idbOpen();
            await new Promise((res, rej) => {
                const tx = db.transaction('state', 'readwrite');
                tx.objectStore('state').put(data, IDB_KEY);
                tx.oncomplete = res; tx.onerror = () => rej(tx.error);
            });
        } catch (e) { console.warn('idb save', e); }
    }
    async function idbLoad() {
        try {
            const db = await idbOpen();
            return await new Promise((res, rej) => {
                const tx = db.transaction('state', 'readonly');
                const r = tx.objectStore('state').get(IDB_KEY);
                r.onsuccess = () => res(r.result || null);
                r.onerror = () => rej(r.error);
            });
        } catch (e) { return null; }
    }
    async function idbClear() {
        try {
            const db = await idbOpen();
            await new Promise((res) => {
                const tx = db.transaction('state', 'readwrite');
                tx.objectStore('state').delete(IDB_KEY);
                tx.oncomplete = res;
            });
        } catch (e) {}
    }

    let autosaveTimer;
    function scheduleAutosave() {
        clearTimeout(autosaveTimer);
        autosaveTimer = setTimeout(captureAndSave, 1500);
    }
    async function captureAndSave() {
        if (!app.konva.stage || !app.state.imageSource) return;
        try {
            const bgCv = document.createElement('canvas');
            bgCv.width = app.state.imgW; bgCv.height = app.state.imgH;
            bgCv.getContext('2d').drawImage(app.konva.imageNode.image(), 0, 0);
            const bgBlob = await new Promise(r => bgCv.toBlob(r, 'image/png'));
            idbSave({
                bg: bgBlob,
                layerJson: app.konva.drawLayer.toJSON(),
                imgW: app.state.imgW, imgH: app.state.imgH,
                counter: app.state.counter,
                savedAt: Date.now(),
            });
        } catch (e) { console.warn('autosave', e); }
    }
    async function checkRecovery() {
        const state = await idbLoad();
        if (!state || !state.bg || (Date.now() - state.savedAt) > 7 * 24 * 3600 * 1000) return;
        const ageMin = Math.round((Date.now() - state.savedAt) / 60000);
        trackFeature('recovery_offered', { age_min: ageMin });
        return new Promise((resolve) => {
            const modal = $('pmRecoveryModal');
            modal.hidden = false;
            const yes = $('pmRecoveryRestore'), no = $('pmRecoveryDiscard');
            const cleanup = (doRestore) => {
                modal.hidden = true;
                yes.removeEventListener('click', onYes);
                no.removeEventListener('click', onNo);
                if (doRestore) {
                    trackFeature('recovery_restored', { age_min: ageMin });
                    restoreSession(state);
                } else {
                    trackFeature('recovery_discarded', { age_min: ageMin });
                    idbClear();
                }
                resolve();
            };
            const onYes = () => cleanup(true);
            const onNo = () => cleanup(false);
            yes.addEventListener('click', onYes);
            no.addEventListener('click', onNo);
        });
    }
    async function restoreSession(state) {
        const url = URL.createObjectURL(state.bg);
        const img = new Image();
        img.onload = () => {
            // The marks go back onto THIS picture's stage, as soon as it exists —
            // at once, or when a late Konva arrives.
            setupWorkspace(img, 'recovered', () => {
                try {
                    if (state.layerJson) {
                        restoreLayer(state.layerJson);
                        app.state.counter = state.counter || 1;
                        pushHistory();
                    }
                } catch (e) { console.warn('restore layer', e); }
            });
        };
        img.src = url;
    }

    // Hook autosave into history/tool actions
    const origPush = pushHistory;
    pushHistory = function(){ origPush.apply(this, arguments); scheduleAutosave(); };

    // ────────────── PWA share target ──────────────
    // Routes the cached file through ToolUploader.inject so it shares the
    // accept-MIME gate (rejects non-image/non-PDF cleanly with a localised
    // toast instead of openImageBlob silently doing nothing on weird types),
    // FileBytes prime, and feature_use(file_picked, src=share_target)
    // telemetry. The 'tool-uploader:file' listener takes over from here —
    // including HEIC and PDF special-cases — so share-target now reaches
    // those formats too, which the old direct openImageBlob path skipped.
    function handleShareTarget() {
        const params = new URLSearchParams(location.search);
        if (params.get('shared') !== '1') return;
        if (!window.caches) return;
        caches.open('share-target').then(async (cache) => {
            const resp = await cache.match('/shared-file');
            if (!resp) return;
            const blob = await resp.blob();
            const filename = decodeURIComponent(resp.headers.get('X-File-Name') || 'shared-image');
            await cache.delete('/shared-file');
            const file = new File([blob], filename, { type: blob.type || 'image/png' });
            if (window.ToolUploader && typeof window.ToolUploader.inject === 'function') {
                window.ToolUploader.inject('pmUpload', file, 'share_target');
            } else if (blob.type && blob.type.startsWith('image/')) {
                openImageBlob(blob, filename); // pre-1.1 fallback
            }
            // Strip ?shared=1 from URL to avoid duplicate load on refresh
            history.replaceState({}, '', location.pathname);
        }).catch(err => console.warn('share target', err));
    }

    // ────────────── Eyedropper ──────────────
    function pickColorAt(point) {
        try {
            const src = app.konva.imageNode.image();
            if (!src) return null;
            const cv = document.createElement('canvas');
            cv.width = src.width || src.naturalWidth;
            cv.height = src.height || src.naturalHeight;
            cv.getContext('2d').drawImage(src, 0, 0);
            const d = cv.getContext('2d').getImageData(
                Math.max(0, Math.min(cv.width - 1, Math.round(point.x))),
                Math.max(0, Math.min(cv.height - 1, Math.round(point.y))), 1, 1
            ).data;
            const hex = '#' + [d[0], d[1], d[2]].map(v => v.toString(16).padStart(2, '0')).join('');
            return hex;
        } catch (e) { console.warn('eyedropper', e); return null; }
    }
    function highlightPaletteSwatch(color) {
        $$('.pm-swatch').forEach(s => s.classList.toggle('is-active', s.dataset.color === color));
    }

    // ────────────── Multi-select & Transformer helpers ──────────────
    function setSelection(nodes) {
        const tr = app.konva.transformer;
        tr.nodes(nodes);
        app.konva.overlayLayer.batchDraw();
    }
    function getSelection() {
        // arrows/Delete before any image exists — no stage, no transformer (Sentry KX)
        const tr = app.konva && app.konva.transformer;
        return tr ? tr.nodes() : [];
    }

    // ────────────── Clipboard (copy/paste/duplicate objects) ──────────────
    const PMClipboard = { items: null };
    function copySelection() {
        const sel = getSelection();
        if (!sel.length) return false;
        PMClipboard.items = sel.map(n => n.toJSON());
        trackFeature('copy_objects', { n: sel.length });
        return true;
    }
    function pasteObjects(offset) {
        if (!PMClipboard.items || !PMClipboard.items.length) return false;
        const layer = app.konva.drawLayer;
        const pasted = [];
        PMClipboard.items.forEach(json => {
            try {
                const n = Konva.Node.create(json);
                // Offset pasted objects so user sees duplication
                const dx = offset?.dx ?? 14, dy = offset?.dy ?? 14;
                n.x((n.x() || 0) + dx);
                n.y((n.y() || 0) + dy);
                n.draggable(true);
                // Re-cache filtered Konva.Image nodes so filter pipeline works again
                if (n.getClassName() === 'Image' && n.getAttr('pmFilterKind')) {
                    try { n.cache(); } catch(_) {}
                }
                layer.add(n);
                pasted.push(n);
            } catch (e) { console.warn('paste node', e); }
        });
        layer.batchDraw();
        setSelection(pasted);
        pushHistory();
        refreshLayerPanel();
        trackFeature('paste_objects', { n: pasted.length });
        return true;
    }
    function duplicateSelection() {
        if (copySelection()) pasteObjects({ dx: 20, dy: 20 });
    }

    // ────────────── Nudge (arrow keys) ──────────────
    function nudgeSelection(dx, dy) {
        const sel = getSelection();
        if (!sel.length) return;
        sel.forEach(n => { n.x(n.x() + dx); n.y(n.y() + dy); });
        app.konva.drawLayer.batchDraw();
        app.konva.overlayLayer.batchDraw();
        scheduleAutosave();
    }

    // ────────────── Lock / visibility toggles (per-object) ──────────────
    function toggleNodeLock(node) {
        const locked = !node.getAttr('pmLocked');
        node.setAttr('pmLocked', locked);
        node.draggable(!locked);
        node.listening(!locked);
        if (locked) {
            // Remove from selection if currently selected
            const sel = getSelection().filter(n => n !== node);
            setSelection(sel);
        }
        app.konva.drawLayer.batchDraw();
        trackFeature('toggle_lock', { locked });
    }
    function toggleNodeVisible(node) {
        const visible = !node.visible();
        node.visible(visible);
        app.konva.drawLayer.batchDraw();
        trackFeature('toggle_visible', { visible });
    }

    // ────────────── Select-all ──────────────
    function selectAll() {
        const nodes = app.konva.drawLayer.find('.pm-obj').filter(n => n.visible() && !n.getAttr('pmLocked'));
        setSelection(nodes);
        trackFeature('select_all', { n: nodes.length });
    }

    // ────────────── Bootstrap ──────────────
    document.addEventListener('DOMContentLoaded', async () => {
        // Konva, the engine of the stage, is a separate file; see «vendor readiness».
        // First, so that nothing below can keep the wait from starting.
        waitForKonva();
        initUpload();
        bindUI();
        updateUndoRedoButtons();

        // A busy editor swallows presses even where `inert` is unknown (Firefox
        // before 112, Safari before 15.5) — no handler under it reads a stage
        // that is not there yet.
        $('pmWorkspace')?.addEventListener('click', (e) => {
            if ($('pmWorkspace').getAttribute('aria-busy') !== 'true') return;
            e.preventDefault();
            e.stopPropagation();
        }, true);
        $('pmBootRetry')?.addEventListener('click', () => {
            try { if (window.__ae) window.__ae('feature_use', { slug: 'photo-markup', f: 'module_boot_reload' }); } catch (_) {}
            location.reload();
        });

        // Extra bindings for wow features
        $('pmCropApply')?.addEventListener('click', applyCrop);
        $('pmCropCancel')?.addEventListener('click', () => { clearCrop(); selectTool('select'); });

        // Spotlight: activate when tool picked
        const origSelectTool = selectTool;
        window.__pmOrigSelect = origSelectTool;

        handleShareTarget();
        await checkRecovery();
    });

    // Expose for debugging / tests
    app.exportPNG = exportPNG;
    app.exportJPG = exportJPG;
    app.exportSVG = exportSVG;
    app.undo = undo;
    app.redo = redo;
    app.selectTool = selectTool;
    app.openImageBlob = openImageBlob;
    // Test-only helper: create a solid-colour canvas + load it as workspace image
    app.loadSampleImage = function(kind) {
        const cv = document.createElement('canvas');
        cv.width = 1280; cv.height = 720;
        const ctx = cv.getContext('2d');
        if (kind === 'photo') {
            const g = ctx.createRadialGradient(640, 360, 60, 640, 360, 720);
            g.addColorStop(0, '#fb923c'); g.addColorStop(0.5, '#db2777'); g.addColorStop(1, '#312e81');
            ctx.fillStyle = g;
        } else {
            const g = ctx.createLinearGradient(0, 0, 0, 720);
            g.addColorStop(0, '#f3f4f6'); g.addColorStop(1, '#e5e7eb');
            ctx.fillStyle = g;
        }
        ctx.fillRect(0, 0, 1280, 720);
        return new Promise(resolve => cv.toBlob(b => { openImageBlob(b, (kind || 'sample') + '.png').then(resolve); }, 'image/png'));
    };
})();
